// Isaac Gaussian-splat avatar, rendered INSIDE the host React-Three-Fiber
// `<Canvas>` scene. Mounted by App.jsx in splat mode — i.e. when the active
// character is a TexAvatars splat character (isSplatCharacter, e.g.
// `isaac-gs`). This is the heavy end of
// the Isaac integration: it owns the TexAvatars inference pipeline (one live
// OGSBranch per region — deform + trunk?→geom→appr + plücker + lifter — closed
// by ONE OGSRenderer) and a SplatMeshUpdater that swaps a
// SplatMesh into the host scene. It reuses every src/ogs/ inference
// module verbatim — only the orchestration (mirrored from texavatars/init.ts's
// runFullPipeline/runAppearance) lives here, rewired to the host camera/scene
// instead of a standalone viewer's private one.
//
// It does NOT own the rig. The rig flows in from the live conversation
// face-graph (finalPose -> arkitToIsaacRig -> isaacSplat.setRigDirect) via the
// isaacSplat handle, which calls back into the `engine` installed here.
// Heavy ONNX/mesh loading happens on mount (only for a splat character),
// never at the plugin-load boot path — so a normal page load pays nothing.

import { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useThree, useFrame } from '@react-three/fiber'
import { SparkRenderer } from '@sparkjsdev/spark'
import { SPARK_TRAINING_BLUR } from '../ogs/render/sparkBlur.js'
import { faceGraphState } from '../lib/faceGraphState.js'
import { evaluateGraph, updateSMTransitions } from '../lib/GraphEvaluator.js'
import { setVariable, variableValues } from '../lib/animVariables.js'
import { faceEvalResult } from '../lib/faceEvalResult.js'
import { faceClipPlayer } from '../lib/FaceClipPlayer.js'
import { debugState } from '../lib/debugState.js'
import { currentBlinkWeight } from '../lib/blinkRuntime.js'
import { getCurrentArkit } from '../network/AudioAnimationBuffer.js'
import { composeRigControlArkit, rigControlState, rigControlWeight } from '../lib/rigControlState.js'
import { registerRigBoard, clearRigBoard, setRigBoardRawLayer, rigBoardSource, resolveRigRange, resolveRigPresetsFile } from '../ogs/rig/rigBoardSource.js'
import { parseNpyFloat32 } from '../lib/npyFloat32.js'
import {
  POSE_ANCHORS_FILE, poseAnchorsFileFor, poseAnchorsEnabled, parsePoseAnchors, createPoseAnchorBlender,
} from '../ogs/inference/poseAnchors.js'
import {
  trainedLimits, parseMouthController, graftClosedMouth, buildBlinkMacro,
} from '../lib/rigBoard.js'
import { resolveRigChain, hasRigChain } from '../lib/rigChain.js'
import { buildMacro, macroSubset, poseRow, deriveEyeRefs } from '../lib/rigBoard.js'
import { getVariable } from '../lib/animVariables.js'
import { arkitToIsaacRig } from '../ogs/rig/arkitToIsaacRig.js'
import {
  buildMouthSubspace, buildRegionalSubspaces, regionKey, resolveRigProjection, chooseProjectionScope,
  subspaceRetention, MIN_EXPRESSION_RETENTION,
} from '../ogs/rig/mouthSubspace.js'
import { gatherFromRigNames } from '../ogs/rig/rigGatherFromNames.js'
import { resolveEyeFollowSlots, buildGazeDrive, applyEyeFollowRig, splatEyePivot } from '../ogs/rig/eyeFollowRig.js'
import { eyeFollowRuntime } from '../lib/eyeFollowRuntime.js'
import { updateEyeFollowFromScene, resetEyeFollowScene } from '../lib/eyeFollowScene.js'
import { setSplatEyeFollowCapability } from '../lib/eyeFollowSupport.js'
import {
  DEFAULT_LIVE_MOUTH_WEIGHT, applyLiveMouthMix, buildLiveMouthDrive, maskLiveArkitRegions, maskArkitRegions,
} from '../ogs/rig/liveRigRegions.js'
import { loadMultiRegionScene } from '../ogs/assets/loadMultiRegionScene'
import { createBranchMeshOverlay } from '../ogs/render/branchMeshOverlay.js'
import { liveRigBranches } from '../ogs/assets/sceneManifest'
import {
  formatPartDriverFit, loadPartDriver, PART_DRIVER_RIG, partRigUrl,
} from '../ogs/inference/partDriver'
import { partDriveRegistry } from '../ogs/preview/partDriveRegistry'
import { applyPartRigPose, formatPartRigPose } from '../ogs/inference/partBodyPose'
import { createGpuPartSkinner, verifyGpuSkinner, MAX_SKIN_DISAGREEMENT_METRES } from '../ogs/inference/GpuPartSkinner'
import { HeadTeethEngine } from '../ogs/inference/headTeethEngine'
import { OGSBranch } from '../ogs/graph/nodes/OGSBranch'
import { branchPortNames } from '../ogs/graph/branchPorts'
import { setContentFramer, fitCameraToSphere } from '../lib/viewportFraming'
import { prepareOgsLiftDevice, resolveOgsLiftDevice } from '../ogs/inference/gpuDevice'
import { resolveFaceDrives, ogsGpuFlags } from '../ogs/inference/ogsGpuFlags'
import { blendRigInto, measureConstraintDelta, rigConstrain, armRigConstrain, setRigConstrain, resetRigConstrain } from '../ogs/rig/rigConstrainBlend'
import { liveFaceControls, armLiveMouthWeight, resetLiveFaceControls } from '../ogs/rig/liveFaceControls.js'
import { chooseLift, liftReport } from '../ogs/inference/myra/myraLiftGate.ts'
import { applyMomentScale } from '../ogs/inference/plucker'
import { SplatMeshUpdater } from '../ogs/render/SplatMeshUpdater'
import { LocalFrameCamera } from '../ogs/render/localFrameCamera'
import { decodePackedPosition, packedAlphaByte } from '../ogs/render/packedSplat'
import { isaacSplat } from '../ogs/render/splatHandle'
import * as ogsOrt from '../ogs/inference/ort'
import { resolveProviders } from '../lib/ortSession.js'
import ogsRendererPlugin from '../../plugins/ogs-renderer/index.js'
import { resolveRenderPhase, branchRegionStates, branchNode, createRenderBlockDetector } from '../ogs/graph/rendererGate'
import { resolveCharacterId } from '../lib/characterState.js'
import { loadOgsContentPacks, probeMeshOnlyBundle } from '../lib/ogsContentPacks.js'
import { fetchAamFile } from '../lib/assetManagerApi.js'
import { MeshClipPreview } from '../ogs/preview/MeshClipPreview.js'
import { mountEyeBranch } from '../ogs/eyes/index.ts'
import { createCameraWatch } from '../ogs/cameraWatch.ts'
import { createFrameSlot } from '../ogs/inference/splatFrameSlot.js'
import { resolveWorldRotation, worldRotationQuaternion } from '../ogs/render/worldRotation.ts'
import { rigToWorldRotation, rigToWorldScale } from '../ogs/inference/rigRenderFrame.js'
import { reportDowngrade, reportPrecision, reportRigProblem } from '../lib/ogsAssetStatus.js'
import { readAvatarPrecision, precisionPrefersFp16 } from '../lib/ogsPrecision.js'
import { isCiMode } from '../lib/ciMode'


// Which branches actually got the myra lift, once every branch has resolved one.
// An error, not a log, when one fell back: it still renders, under the shared
// rules, and that reads as a working avatar — the thing this gate exists to catch.
function reportLift(tag, choice, branches) {
  const report = liftReport(choice, branches.map((br) => ({ name: br.name, kind: br.liftKind() })))
  if (!report) return
  const line = `[${tag}] ${report.text}`
  if (report.phase === 'error') console.error(line)
  else console.info(line)
}

// Cull splats that would crash the WebGL/Spark renderer when a FOREIGN clip
// deforms the mesh: non-finite (degenerate jacobian) or wildly over-stretched
// (a thin/inverted triangle blows the covariance → a giant smear). Mirrors
// render_macarena's per-frame cull_cov_pct + hidden-texel masks. Zeros opacity +
// collapses the splat so the count is stable but the bad splat draws nothing.
function sanitizeDrivenGaussians(g, maxScale = 0.15) {
  const { positions: p, scales: s, rotations: r, opacities: o, count } = g
  let culled = 0
  for (let i = 0; i < count; i++) {
    const p3 = i * 3, r4 = i * 4
    const bad =
      !Number.isFinite(p[p3]) || !Number.isFinite(p[p3 + 1]) || !Number.isFinite(p[p3 + 2]) ||
      !Number.isFinite(s[p3]) || !Number.isFinite(s[p3 + 1]) || !Number.isFinite(s[p3 + 2]) ||
      !Number.isFinite(r[r4]) || !Number.isFinite(r[r4 + 1]) || !Number.isFinite(r[r4 + 2]) || !Number.isFinite(r[r4 + 3]) ||
      s[p3] > maxScale || s[p3 + 1] > maxScale || s[p3 + 2] > maxScale
    if (!bad) continue
    culled++
    o[i] = 0
    p[p3] = 0; p[p3 + 1] = 0; p[p3 + 2] = 0
    s[p3] = 1e-6; s[p3 + 1] = 1e-6; s[p3 + 2] = 1e-6
    r[r4] = 0; r[r4 + 1] = 0; r[r4 + 2] = 0; r[r4 + 3] = 1
  }
  return culled
}


// Find an existing SparkRenderer in the scene (HairSplat / SplatEnvironment may
// own one) and reuse it — two SparkRenderers double-render. Only create+add a
// new one if none exists. Returns { spark, owned }.
function findOrCreateSpark(scene, gl) {
  let found = null
  scene.traverse((c) => { if (!found && c instanceof SparkRenderer) found = c })
  if (found) return { spark: found, owned: false }
  if (gl.__hairSparkRenderer) {
    if (!gl.__hairSparkRenderer.parent) scene.add(gl.__hairSparkRenderer)
    return { spark: gl.__hairSparkRenderer, owned: false }
  }
  const sr = new SparkRenderer({ renderer: gl, ...SPARK_TRAINING_BLUR })
  scene.add(sr)
  return { spark: sr, owned: true }
}

// Frame the host camera + OrbitControls around the visible splats. Mirrors
// init.ts __autoFrame: percentile bounds (5/95) on splats whose opacity logit
// > -1.386 (sigmoid > 0.2), ignoring UV-seam outliers. lifter.out positions are
// in the training cm frame; worldScale maps the centroid/radius into the scene's
// meter frame so the camera frames the (scaled) SplatMesh, not its cm phantom.
// Percentile bounds (5/95) ignore UV-seam outliers; the fit itself is
// viewportFraming.fitCameraToSphere, shared with the rig path.
function frameCameraToSplats(camera, lifter, worldScale = 1, worldOffset = [0, 0, 0], { preserveDirection = false, worldRotation = null } = {}) {
  const g = lifter.out
  const xs = [], ys = [], zs = []
  if (g.packed) {
    // packedMode lift emits only the sparkjs packed words (no SoA out.positions).
    // Decode positions and gate on the alpha byte (sigmoid(op)*255; sigmoid>0.2
    // ⟺ byte≥51 — the packed equivalent of the logit>-1.386 test).
    for (let i = 0; i < g.count; i++) {
      if (packedAlphaByte(g.packed, i) < 51) continue
      const [px, py, pz] = decodePackedPosition(g.packed, i)
      xs.push(px); ys.push(py); zs.push(pz)
    }
  } else {
    for (let i = 0; i < g.count; i++) {
      if (g.opacities[i] < -1.386) continue
      xs.push(g.positions[i * 3 + 0])
      ys.push(g.positions[i * 3 + 1])
      zs.push(g.positions[i * 3 + 2])
    }
  }
  if (!xs.length) return false
  xs.sort((a, b) => a - b); ys.sort((a, b) => a - b); zs.sort((a, b) => a - b)
  const pct = (a, p) => a[Math.min(a.length - 1, Math.floor(a.length * p)) | 0]
  const xMin = pct(xs, 0.05), xMax = pct(xs, 0.95)
  const yMin = pct(ys, 0.05), yMax = pct(ys, 0.95)
  const zMin = pct(zs, 0.05), zMax = pct(zs, 0.95)
  // Same placement the SplatMesh gets (applyMeshPlacement): scale, then rotate,
  // then offset. The radius needs no rotation — it is the box's half-diagonal,
  // which turns with it.
  const centre = new THREE.Vector3(
    (xMin + xMax) / 2 * worldScale,
    (yMin + yMax) / 2 * worldScale,
    (zMin + zMax) / 2 * worldScale,
  )
  if (worldRotation) centre.applyQuaternion(worldRotation)
  const cx = centre.x + worldOffset[0]
  const cy = centre.y + worldOffset[1]
  const cz = centre.z + worldOffset[2]
  const radius = Math.hypot(
    (xMax - xMin) / 2 * worldScale,
    (yMax - yMin) / 2 * worldScale,
    (zMax - zMin) / 2 * worldScale,
  )
  return fitCameraToSphere(camera, window.__ORBIT_CONTROLS__, [cx, cy, cz], radius, { preserveDirection })
}

// Same framing math as frameCameraToSplats, from a mesh preview's world Box3.
function frameCameraToBox(camera, box) {
  const c = box.getCenter(new THREE.Vector3())
  const s = box.getSize(new THREE.Vector3())
  const radius = Math.max(s.x, s.y, s.z) / 2
  if (!Number.isFinite(radius) || radius <= 0) return false
  const dist = (radius / Math.tan((camera.fov * Math.PI) / 360)) * 1.5
  camera.position.set(c.x, c.y, c.z + dist)
  camera.near = Math.max(0.001, dist * 0.001)
  camera.far = dist * 1000
  camera.lookAt(c.x, c.y, c.z)
  camera.updateProjectionMatrix()
  const controls = window.__ORBIT_CONTROLS__
  if (controls) {
    controls.target.copy(c)
    controls.minDistance = dist * 0.01
    controls.maxDistance = dist * 100
    controls.update()
  }
  return true
}

export default function OGSModel({ animationBuffer, onReady, onError, onRenderBlocked } = {}) {
  const gl = useThree((s) => s.gl)
  const scene = useThree((s) => s.scene)
  const camera = useThree((s) => s.camera)

  // Avatar root — the one object a splat character's placement lives on
  // (ENG-895). Every splat this component mounts (SplatMeshUpdater meshes, dyno
  // generators) parents HERE rather than straight into the scene, so the
  // transform gizmo has a stable target that the render pipeline doesn't fight:
  // SplatMeshUpdater keeps re-applying each mesh's local meshScale/meshOffset on
  // every rebuild, which is exactly why the gizmo can't own the mesh itself.
  //
  // Deliberately NOT the SparkRenderer — that's a shared renderer other splats
  // (hair, environment) also live under, and moving it would drag them along.
  const avatarRoot = useMemo(() => {
    const g = new THREE.Group()
    g.name = 'ogs-avatar-root'
    return g
  }, [])
  useEffect(() => {
    scene.add(avatarRoot)
    // Published for the transform adapter + tests. Distinct from
    // __AVATAR_GROUP__, which is the GLB rig's group and carries movement /
    // floor semantics this doesn't have.
    window.__OGS_AVATAR_ROOT__ = avatarRoot
    return () => {
      scene.remove(avatarRoot)
      if (window.__OGS_AVATAR_ROOT__ === avatarRoot) delete window.__OGS_AVATAR_ROOT__
    }
  }, [scene, avatarRoot])

  // Camera expressed in the avatar's local frame, for the view-dependent
  // appearance decoder. Reused every frame (no per-frame alloc). See
  // localFrameCamera for why feeding the world camera would corrupt shading once
  // the avatar can be transformed.
  const localCam = useMemo(() => new LocalFrameCamera(), [])
  // Its own instance for the per-frame watch, so a pass mid-await never reads a watch update.
  const watchCam = useMemo(() => new LocalFrameCamera(), [])

  // Animation-variable debug globals. AvatarModel normally exposes these, but
  // it isn't mounted in splat mode — the event graph + tests still read/write
  // variables (e.g. LiveLinkWeight driving the facerig node's live lip-sync),
  // so expose the same globals here for parity. (Additive — never removes the
  // AvatarModel ones; only one of the two components is mounted at a time.)
  useEffect(() => {
    window.__SET_VARIABLE__ = setVariable
    window.__VARIABLE_VALUES__ = variableValues
    // Splat-path anchor: OGSModel only mounts when the character resolves to
    // splat mode (App gates it on splatMode), so this proves the /ogs bundle
    // path was chosen over the rig / rig-missing path — independent of whether
    // the blueprint's OGS render chain is wired yet.
    window.__OGS_MODEL_MOUNTED__ = true
    return () => { delete window.__OGS_MODEL_MOUNTED__ }
  }, [])

  // Splat face sink — the splat-mode equivalent of HeadModel.
  //
  // OGSModel is to the splat what HeadModel is to Myra's morph mesh: it reads
  // the SAME face-graph output and applies it to the character's rig. The face
  // graph (LiveLink node + conversational/breathing/fidget state machines +
  // blink, identical to Myra's) evaluates to ARKit weights; we combine them the
  // way HeadModel does (live LiveLink while talking, idle face clips otherwise,
  // plus blink) into a 52-ARKit frame, then convert that to the character's
  // N-control MetaHuman rig (arkit_to_mh + gather) and feed the splat. The
  // ARKit->rig conversion is the renderer's job here, exactly as ARKit->morphs
  // is in HeadModel — there is no graph "facerig" node.
  //
  // The gather (and so the rig dim) is derived from the BUNDLE's rig_names.json
  // once the content packs load (setLipSyncGather below): isaac V1 declares 60
  // controls, the V2 head+teeth export 174. A bundle without a resolvable
  // rig_names.json leaves gatherRef null — driveSplatFace no-ops (idle/neutral
  // face), reported loudly at load, never a wrong-dim push the engine has to
  // discard.
  const gatherRef = useRef(null)
  const arkitRef = useRef(new Float32Array(52))
  const rigRef = useRef(null)
  // The bundle's rest configuration + declared control domain, resolved once at
  // init. Used by BOTH the deform anchor and the live ARKit path: 'what does
  // this control space consider neutral' is one fact, and answering it twice
  // differently is how the two ended up disagreeing.
  const restRigRef = useRef(null)
  const rigRangeRef = useRef(null) // Float32Array sized to the gather, set with it
  // The live per-pose anchor blender, for teardown + the debug global.
  const poseAnchorBlenderRef = useRef(null)
  // Trained mouth subspace + box, the expressivity-preserving constraint.
  const mouthPcaRef = useRef(null)
  // Blink driven as a trained lid CONFIGURATION (see buildBlinkMacro).
  const blinkMacroRef = useRef(null)
  const scratch188Ref = useRef(new Float32Array(188))
  // ?rigconstrain=<0..1> — how much of the trained clamp + subspace projection
  // reaches the rig. 1 = the shipped face, 0 = the same frame with neither
  // (identical to ?rigclamp=none&rigpca=off). Below 1, driveSplatFace composes a
  // second, unconstrained twin into `looseRigRef` and blends the two results;
  // preallocated with the rig itself, so the blend costs no per-frame allocation.
  // The WEIGHT itself lives in the rigConstrain module singleton, not a ref: the
  // Rig Preview panel edits it live and the render loop reads it every frame.
  const looseRigRef = useRef(null)
  // Live-path region control (src/ogs/rig/liveRigRegions.js): the trained mouth
  // references for this bundle (built with the rest rig in resolveRestRig's
  // caller), and the session flags. `?regionmask=0` re-admits the A2F eye/brow
  // channels; `?livemouth=<0..1>` sets the manifold weight (0 disables the mix).
  const liveMouthDriveRef = useRef(null)
  const liveFaceFlagsRef = useRef(null)
  // What ?livemouth asked for, held until the pipeline can arm the store with it.
  // NULL when ?livemouth was not typed, so the DEFAULT is applied by
  // armLiveMouthWeight from the one place it is stated. A numeric fallback here
  // would be a second copy of it, free to drift from the canonical one.
  const urlMouthWeightRef = useRef(null)
  if (!liveFaceFlagsRef.current) {
    const qp = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search) : new URLSearchParams()
    const lm = Number.parseFloat(qp.get('livemouth') ?? '')
    const ogsFlags = ogsGpuFlags()
    liveFaceFlagsRef.current = {
      regionMask: qp.get('regionmask') !== '0',
      lastT: 0, // last aperture coordinate, for the debug global
      // The two EYE-REGION drives, one off switch each. Resolved by ogsGpuFlags —
      // that module exists so a flag's meaning lives in ONE place — and copied onto
      // this object so the per-frame path costs a property read and the pair shows
      // up on __LIVE_FACE_REGIONS__ beside the mouth knobs they sit next to.
      // Resolved URL > bundle > default. The bundle half cannot be known here (the
      // manifest is fetched), so this is the URL layer and applyBundleFaceDrives
      // re-runs the same resolver once the manifest lands.
      gaze: ogsFlags.gaze,
      blink: ogsFlags.blink,
      regions: null,
    }
    // The mouth weight lives in the liveFaceControls singleton, because the Face rig
    // row edits it live — and it is exposed here as a LIVE GETTER rather than copied,
    // so __LIVE_FACE_REGIONS__ can never disagree with what the frame path applied.
    // The URL is read here and armed at pipeline start (where the bundle's own mouth
    // drive is known, which is what decides whether the control is offered at all).
    Object.defineProperty(liveFaceFlagsRef.current, 'mouthWeight', {
      enumerable: true, get: () => liveFaceControls.mouthWeight,
    })
    urlMouthWeightRef.current = Number.isFinite(lm) ? Math.min(1, Math.max(0, lm)) : null
    if (typeof window !== 'undefined') window.__LIVE_FACE_REGIONS__ = liveFaceFlagsRef.current
  }
  // A bundle may DECLINE an eye-region drive its capture cannot take (scene.json
  // `face_drives`) — the same answer for everyone who opens that character, so it does
  // not belong in a URL each of them has to know to type. An explicit ?gaze=/?blink=
  // still wins, in both directions, and a bundle that declares nothing changes nothing.
  const applyBundleFaceDrives = (manifest) => {
    const search = typeof window !== 'undefined' ? window.location.search : ''
    const drives = resolveFaceDrives(search, manifest?.faceDrives)
    liveFaceFlagsRef.current.gaze = drives.gaze
    liveFaceFlagsRef.current.blink = drives.blink
    // Carried so a stand-down can name what decided it: "?gaze=0" is a lie when the
    // bundle declined the drive and there is no flag in the URL to drop.
    liveFaceFlagsRef.current.source = drives.source
    // Only the ARKit channel GROUPS — gaze and blink are drives we generate, and are
    // gated at their own call sites rather than by zeroing channels.
    liveFaceFlagsRef.current.regions = { brow: drives.brow, eye: drives.eye, cheek: drives.cheek, nose: drives.nose }
    for (const key of ['gaze', 'blink']) {
      // Only the bundle's own decisions are worth a line: a default is the status quo
      // and a URL flag is something the reader just typed.
      if (drives.source[key] !== 'bundle') continue
      console.info(`[ogs] ${key} drive ${drives[key] ? 'ON' : 'OFF'} — declared by this bundle `
        + `(scene.json face_drives.${key}); ?${key}=${drives[key] ? '0' : '1'} overrides it`)
    }
  }
  const lastRigRef = useRef(null)
  const haveSentRef = useRef(false)
  // Eye Follow on the splat rig path (ENG-1060, src/ogs/rig/eyeFollowRig.js):
  // which slots of THIS bundle's control space the eye aim writes (resolved
  // beside the gather from the same rig_names.json), the aim origin in
  // avatar-root-local space (the eye midpoint, computed once per pipeline from
  // branch geometry + the mesh placement), and the bundle's DECLARED range —
  // the eye stage clamps to it, deliberately not to trainedLimits (isaac's
  // trained data freezes the eye controls, so the trained box has zero width
  // there and would erase the aim; see the eyeFollowRig.js header).
  const eyeFollowSlotsRef = useRef(null)
  const eyeFollowPivotRef = useRef(null)
  const declaredRangeRef = useRef(null)

  // Load face clips (idle/breathing/fidget/gesture faces) once the avatar is up,
  // so the state machines can drive the splat at idle — same source HeadModel
  // uses. Best-effort: if a splat character ships no face clips, getBlendedWeights
  // returns null and idle simply falls back to neutral + blink; lip-sync (the
  // LiveLink path) is unaffected.
  const faceClipsStartedRef = useRef(false)
  useEffect(() => {
    if (faceClipsStartedRef.current) return
    faceClipsStartedRef.current = true
    faceClipPlayer.loadAll().catch(e => console.warn('[isaac] face clips load failed (idle stays neutral):', e?.message ?? e))
  }, [])

  // Derive the ARKit-52 -> N-control lip-sync gather from the bundle's own
  // rig_names.json (called by startPipeline once the content packs load). Until
  // it lands, driveSplatFace no-ops — the initial neutral kick still shows the
  // face. Loud failure, no fallback gather: a wrong-space gather would decode
  // garbage; a missing one keeps the face idle/neutral and says so.
  function setLipSyncGather(rigNames) {
    try {
      if (!rigNames) throw new Error('bundle has no rig_names.json')
      const g = gatherFromRigNames(rigNames)
      gatherRef.current = g
      rigRef.current = new Float32Array(g.length)
      looseRigRef.current = new Float32Array(g.length)
      lastRigRef.current = new Float32Array(g.length)
      haveSentRef.current = false
      // Same source, same moment: which slots Eye Follow writes is a fact of
      // this rig_names.json, exactly like the gather.
      eyeFollowSlotsRef.current = resolveEyeFollowSlots(rigNames)
      console.info(`[ogs] lip-sync gather ready: ARKit-52 → ${g.length} rig controls (bundle rig_names.json)`)
    } catch (e) {
      gatherRef.current = null
      rigRef.current = null
      looseRigRef.current = null
      lastRigRef.current = null
      eyeFollowSlotsRef.current = null
      console.error('[ogs] lip-sync gather unavailable — face stays idle/neutral:', e?.message ?? e)
    }
  }

  // Blueprint renderer-chain phase — the graph is the pipeline, so the renderer
  // node drives INIT + RENDER, not just visibility:
  //   'three'     no renderer node → base three.js only, nothing OGS loads
  //   'created'   node present but unwired → SparkRenderer created, idle
  //   'rendering' full chain wired → the inference pipeline runs
  // applyPhase (installed by the init effect) owns the transitions; checkPhase
  // just detects them per frame, memoized on the editor's nodes+edges identity,
  // so the idle per-frame cost is two reference compares.
  const phaseNodesRef = useRef(null)
  const phaseEdgesRef = useRef(null)
  const phaseRef = useRef('three')
  const gateActiveRef = useRef(false)
  const applyPhaseRef = useRef(null)
  // Live decoder-precision swap: the OGS Avatar node's fp16/fp32 pick is applied
  // by rebuilding the decoders in place (no page reload). appliedPrecisionRef is
  // the precision currently BUILT; applyPrecisionRef is the engine hook installed
  // once the pipeline is up. Both null until then, so edits made during load are
  // picked up by startPipeline's own readAvatarPrecision at init.
  const appliedPrecisionRef = useRef(null)
  const applyPrecisionRef = useRef(null)
  // Live region toggles: the head+teeth engine installs this hook; blueprint
  // edits (enable/disable/rewire a plugin.ogs.branch node) re-resolve which
  // regions render. Only called on a nodes/edges identity change.
  const applyRegionsRef = useRef(null)
  function checkPhase() {
    if (faceGraphState.nodes === phaseNodesRef.current && faceGraphState.edges === phaseEdgesRef.current) return phaseRef.current
    phaseNodesRef.current = faceGraphState.nodes
    phaseEdgesRef.current = faceGraphState.edges
    const next = resolveRenderPhase(faceGraphState.nodes, faceGraphState.edges)
    if (next !== phaseRef.current) {
      phaseRef.current = next
      applyPhaseRef.current?.(next)
    }
    // A precision pick change re-renders in place — the same edit that swaps the
    // nodes array carries the new node.data.precision.
    if (phaseRef.current === 'rendering' && applyPrecisionRef.current) {
      const p = readAvatarPrecision(faceGraphState.nodes)
      if (p !== appliedPrecisionRef.current) {
        appliedPrecisionRef.current = p
        applyPrecisionRef.current(p)
      }
    }
    if (phaseRef.current === 'rendering' && applyRegionsRef.current) {
      applyRegionsRef.current(faceGraphState.nodes, faceGraphState.edges)
    }
    return phaseRef.current
  }

  // Rig-chain driving. The bundle's pose table is what macros and trained poses
  // are built from, so it is pulled on demand the first time a graph needs it
  // (the panel does the same) rather than at boot.
  const chainMacrosRef = useRef({ key: null, macros: null })
  const presetsRequestedRef = useRef(false)

  function rigChainMacros() {
    const { presets, names, dim, macros } = rigBoardSource
    if (!presets || !names) return null
    const key = `${dim}:${presets.length}`
    if (chainMacrosRef.current.key === key) return chainMacrosRef.current.macros
    const built = {}
    const o = macros?.mouth_open_pose, c = macros?.mouth_closed_pose
    if (o !== undefined && c !== undefined) {
      built.mouth = buildMacro({
        subset: macroSubset(names, 'mouth'),
        openRef: poseRow(presets, dim, o),
        closedRef: poseRow(presets, dim, c),
      })
    }
    const eyeRefs = deriveEyeRefs(names, presets, dim)
    if (eyeRefs) {
      built.eyes = buildMacro({
        subset: macroSubset(names, 'eyes'),
        openRef: poseRow(presets, dim, eyeRefs.openRow),
        closedRef: poseRow(presets, dim, eyeRefs.closedRow),
      })
    }
    chainMacrosRef.current = { key, macros: built }
    return built
  }

  /** @returns {boolean} true when the blueprint's rig chain drove this frame. */
  function driveRigChain() {
    const { nodes, edges } = faceGraphState
    if (!hasRigChain(nodes)) return false
    if (!rigBoardSource.names) return false

    if (!rigBoardSource.presets && !presetsRequestedRef.current && rigBoardSource.loadPresets) {
      presetsRequestedRef.current = true
      rigBoardSource.loadPresets().catch(e =>
        console.error('[ogs] rig chain: trained poses unavailable —', e?.message ?? e))
    }

    const finalEdge = edges.find(e => e.target === 'final')
    if (!finalEdge) return false
    const pose = resolveRigChain(finalEdge.source, { nodes, edges }, {
      dim: rigBoardSource.dim,
      presets: rigBoardSource.presets,
      names: rigBoardSource.names,
      macros: rigChainMacros(),
      getVariable,
    })
    if (!pose || pose.length !== rigBoardSource.dim) return false
    if (rigChanged(pose)) {
      isaacSplat.setRigDirect(pose)
      if (lastRigRef.current?.length === pose.length) lastRigRef.current.set(pose)
      haveSentRef.current = true
    }
    return true
  }

  /**
   * The rig whose BAKED mesh is the bundle's `neutral_vertices` — the only rig at
   * which `corr = neutral − M·deform(base)` is exact.
   *
   * Row 0 of the pose table, because that is what the export bakes the neutral
   * from (verified on eyeline V10-C: neutral_vertices equals baked frame 0 to
   * 0.00 mm). Returns null when the bundle ships no pose table, and the caller
   * then keeps the zero-rig anchor — correct for a control space centred on 0.
   *
   * The table is ~165 KB, so fetching it at init costs nothing next to the
   * decoders; the rig board still loads its own copy lazily for the pose player.
   */
  async function resolveRestRig(packs, scene) {
    // The bundle's own pose table. `scene.rig_presets` is the top-level one the
    // pose player reads; a bundle that ships none still has the HEAD branch's
    // `rig_presets` (`rig_params_head.npy`), which is the same table — on
    // eyeline-v10c the two files are byte-for-byte the same size and content.
    // Falling back to it is what gives such a bundle a rest rig at all: without
    // one the deform anchors at the ZERO rig, which for a [0, 1] control board is
    // an extreme corner, and every trained-pose consumer downstream (the ARKit
    // base, trained limits, the subspace fit, the live mouth drive) goes dark.
    const file = resolveRigPresetsFile(scene)
    if (!file || !packs?.fetchBytes) return null
    try {
      const { data, rows, cols } = parseNpyFloat32(await packs.fetchBytes(file))
      if (!cols || data.length < cols) return null
      // WIDTH MUST MATCH the control space. A narrower table would be
      // zero-padded by HeadTeethEngine.trim() — silently re-introducing the
      // "zero is an extreme corner" bug this rest rig exists to fix — while
      // arkitToIsaacRig would ignore it outright, so the geometry anchor and the
      // live rig would disagree. Refuse it instead; both consumers then fall
      // back to the same (zero) behaviour rather than to different ones.
      const width = packs?.rigNames?.length ?? 0
      if (width && cols !== width) {
        console.warn(`[ogs-ht] ${file} is ${cols} controls but the rig is ${width} — ignoring it and `
          + `anchoring at the ZERO rig, which is only correct if this control space rests at zero`)
        return null
      }
      // The full table rides along for the live mouth drive (liveRigRegions),
      // the trained-domain limits, the subspace fit and the ARKit base: it is
      // already downloaded for row 0, so keeping it costs nothing. Row count is
      // `presets.length / dim` wherever a consumer needs it.
      return { rest: data.slice(0, cols), presets: data, dim: cols }
    } catch (e) {
      // Loud, not silent: falling back to zeros mis-anchors every pose, so the
      // reason has to be visible rather than inferred from a wrong-looking face.
      console.warn(`[ogs-ht] rest rig unavailable (${file}) — anchoring the deform at the ZERO rig, `
        + `which is only correct if this control space rests at zero:`, e)
      return null
    }
  }

  /**
   * One rig per ARKit channel, driven to 1 through THIS bundle's own gather and
   * clamps — the expressions the live path is made of, and so the right sample to
   * judge a constraint by. Composed onto `base` exactly as driveSplatFace does, so
   * a measurement over these is a measurement of what speech will get.
   *
   * Returns [] before the gather resolves, which makes any measurement over it
   * null rather than a confident zero.
   */
  function arkitChannelPoses(base) {
    const gather = gatherRef.current
    if (!gather || !base?.length) return []
    const arkit = new Float32Array(52)
    const rig = new Float32Array(base.length)
    const out = []
    for (let ch = 0; ch < 52; ch++) {
      arkit.fill(0)
      arkit[ch] = 1
      arkitToIsaacRig(arkit, gather, rig, scratch188Ref.current, base, rigRangeRef.current)
      out.push(Float32Array.from(rig))
    }
    return out
  }

  /**
   * Install the bundle's baked per-pose rig→checkpoint anchors, if it ships any.
   *
   * OPT-IN via `?poseanchor=1`, and deliberately NOT presence-based like the
   * other optional bundle members (the fp16 decoders, the ORL pack). A blink
   * reselects the nearest trained poses, and those carry different head
   * placements, so leaving these on steps the head ~5 mm in one frame every time
   * the character blinks — see poseAnchorsEnabled for the measurements. A bundle
   * that ships the file renders exactly as one that does not until asked.
   *
   * Every refusal is REPORTED and then degrades to that same neutral anchor. The
   * failure this guards is silent — anchors baked against another rig, another
   * pose table, or an older export produce entirely plausible numbers pointing at
   * the wrong poses, and the result is a face subtly in the wrong place with
   * nothing on screen to say so.
   */
  function installPoseAnchors(engine, packs, poseTable) {
    // LIVE getters, defined once. `Object.assign` would invoke them and copy the
    // VALUES, freezing the readout at whatever the blender held the instant it was
    // published — which reads as "installed but never running" and is exactly the
    // kind of wrong that a debug global must not be.
    const state = { state: 'none', count: 0, rig: packs?.rigPlan?.rig ?? null }
    let live = null
    Object.defineProperties(state, {
      pose: { enumerable: true, get: () => live?.pose ?? -1 },
      weights: { enumerable: true, get: () => live?.weights ?? [] },
      lin: { enumerable: true, get: () => (live ? Array.from(live.lin) : null) },
      offset: { enumerable: true, get: () => (live ? Array.from(live.offset) : null) },
    })
    const publish = (extra) => {
      Object.assign(state, extra)
      window.__POSE_ANCHORS__ = state
    }
    publish({})
    // Prefer the file baked for the rig that actually resolved; fall back to the
    // unsuffixed name for bundles baked before anchors were per-rig.
    const rigNow = packs?.rigPlan?.rig ?? 'unknown'
    const bytes = packs?.getBytes?.(poseAnchorsFileFor(rigNow)) ?? packs?.getBytes?.(POSE_ANCHORS_FILE)
    if (!bytes) return
    if (!poseAnchorsEnabled(window.location.search)) {
      console.info('[ogs-ht] pose anchors: off (the bundle ships them; ?poseanchor=1 opts in) '
        + '— using the single neutral anchor')
      publish({ state: 'disabled' })
      return
    }
    if (!poseTable) {
      // The anchors are indexed BY the pose table, so without one there is
      // nothing to locate the live rig against.
      console.warn('[ogs-ht] pose anchors: the bundle ships anchors but no readable pose table — ignoring them')
      publish({ state: 'error', error: 'no pose table' })
      return
    }
    const rig = rigNow
    try {
      const doc = JSON.parse(new TextDecoder().decode(bytes))
      // A rig MISMATCH is expected and benign — anchors are fitted per rig, and a
      // bundle carrying the exact rig's set is simply silent while rig2mesh is
      // driving. Said at info level and given its own state, because routing it
      // through the failure path below would print an error on every default load
      // of a perfectly healthy character, which is how warnings stop being read.
      if (doc?.rig && doc.rig !== rig) {
        console.info(`[ogs-ht] pose anchors: baked for the '${doc.rig}' rig, this load is on '${rig}' — not applied`);
        publish({ state: 'other-rig', bakedFor: doc.rig })
        return
      }
      const numPoses = poseTable.presets.length / poseTable.dim
      const anchors = parsePoseAnchors(doc, {
        rig,
        numPoses,
        presetCols: poseTable.dim,
      })
      const blender = createPoseAnchorBlender({
        anchors,
        presets: poseTable.presets,
        presetRows: numPoses,
        presetCols: poseTable.dim,
      })
      engine.setPoseAnchors(blender)
      poseAnchorBlenderRef.current = blender
      const r = anchors.residual
      console.info(`[ogs-ht] pose anchors: ${anchors.count} baked poses for the '${anchors.rig}' rig`
        + (r && Number.isFinite(r.p50) ? ` (bake residual p50 ${r.p50.toExponential(2)} / p95 ${r.p95.toExponential(2)})` : ''))
      live = blender
      publish({ state: 'applied', count: anchors.count, residual: r })
    } catch (e) {
      console.error(`[ogs-ht] pose anchors REFUSED — falling back to the single neutral anchor: ${e?.message ?? e}`)
      publish({ state: 'error', error: String(e?.message ?? e) })
    }
  }

  function driveSplatFace(delta) {
    const gather = gatherRef.current
    if (!gather) return // gather not loaded yet
    // Raw rig board owns the rig vector while it is armed. One writer per frame:
    // the board pushes absolute control values, so an ARKit frame pushed on top
    // would fight it and the splat would flicker between the two.
    if (rigControlState.rawMode) return

    // Blueprint rig chain (ENG-946): when the face graph composes POSES, that
    // is the face — it replaces the ARKit path entirely rather than layering,
    // because both write the same absolute control vector.
    const chained = driveRigChain()
    if (chained) return

    // ── Evaluate the face graph (same as AvatarModel does for Myra) ──
    updateSMTransitions(faceGraphState)
    const { weights: faceWeights } = evaluateGraph(faceGraphState)
    const liveWeight = faceWeights?.['__livelink__'] ?? 0
    // Export for any other consumer + debug HUD parity with Myra.
    faceEvalResult.liveWeight = liveWeight
    faceEvalResult.weights = faceWeights ?? null
    // Editor parity too: BlueprintEditor's node weight bars read
    // debugState.faceWeights, which only AvatarModel used to publish — so on a
    // splat character every face-graph node (blink, blends, eyeFollow) showed
    // "idle" while it was actually driving. The tick bump matters as much as
    // the weights: every tick++ lives inside applyWeights (the GLB mixer
    // application, never called here), so the editor's 100ms poll gated on
    // debugState.tick never fired on a splat character and every node readout
    // froze at its boot value.
    debugState.faceWeights = faceWeights ?? {}
    debugState.tick++
    // Expose face-clip timings so RuleEvaluator (AnimTimeRemaining) can read
    // them, same as HeadModel does for Myra.
    faceClipPlayer.writeTimingsInto(debugState.clipTimings)

    // ── Combine into a 52-ARKit frame (mirror of HeadModel's selection) ──
    const arkit = arkitRef.current
    arkit.fill(0)
    if (liveWeight > 0) {
      // Talking: live A2F ARKit, crossfaded from neutral by the LiveLink weight.
      const live = getCurrentArkit()
      if (live) for (let i = 0; i < 52; i++) arkit[i] = (live[i] ?? 0) * liveWeight
      // Region control (liveRigRegions): the A2F stream's eye/brow/cheek
      // channels are measured noise, not signal — zeroing their deltas holds
      // those regions at rest, so speech cannot move eyes or brows. The blink
      // overlay below writes AFTER this, so procedural blinks still land.
      // Applies to the LIVE frame only: face clips and Rig Preview frames are
      // authored, including their eye channels.
      if (liveFaceFlagsRef.current.regionMask) maskLiveArkitRegions(arkit)
    } else {
      // Idle: blended face clips driven by the state machines (neutral if none).
      const clip = faceClipPlayer.getBlendedWeights(faceWeights ?? {})
      if (clip) for (let i = 0; i < 52; i++) arkit[i] = clip[i] ?? 0
    }

    // Test seam — the splat sibling of `__FORCE_ARKIT__` on the GLB morph path. A fixed
    // 52-channel frame standing in for the one the face graph just produced, so a spec or
    // a probe can sweep chosen expressions instead of waiting for the idle animation to
    // wander into the pose it wants to measure. Measuring on the idle animation is not a
    // substitute: it is stochastic, and three identical unconstrained runs produced 0, 50
    // and 0 oversized splats, so arms compared over wall-clock windows are not comparable.
    // It REPLACES THE SOURCE and nothing else — the region mask, the blink overlay and the
    // whole rig chain below still run, which is what makes a sweep measure the shipped
    // path rather than a shortcut around it. Ignored unless set.
    const forcedArkit = typeof window !== 'undefined' ? window.__OGS_FORCE_ARKIT__ : null
    if (forcedArkit?.length === 52) for (let i = 0; i < 52; i++) arkit[i] = forcedArkit[i] ?? 0

    // Rig Controller (ENG-946): when the face graph's rigController node is
    // active, the Rig Preview sliders replace the frame selected above — or,
    // in passthrough mode, the live stream composed through the locked
    // channels. Blink still overlays after this, matching HeadModel's layering.
    // With no such node the panel can arm direct drive instead (ENG-1027);
    // rigControlWeight owns that precedence.
    const rigWeight = rigControlWeight(faceWeights)
    if (rigWeight > 0) {
      const frame = composeRigControlArkit(getCurrentArkit())
      for (let i = 0; i < 52; i++) arkit[i] = frame[i] * rigWeight
    }

    // Regions this bundle's capture cannot take, held at REST (scene.json face_drives).
    // Applies to the authored frame too — face clips and the idle animation — because
    // that is where the damage is: on a 30-row table the brow block gets a full-rank
    // fit, so the subspace cannot constrain it, and over a deterministic sweep of the 52
    // single-channel ARKit poses EVERY pose that produced oversized head splats was a
    // brow or eye one (brow alone 192 of 247) while not one mouth channel did. Before
    // the blink overlay, so a procedural blink still reaches the rig — the same ordering
    // maskLiveArkitRegions relies on.
    maskArkitRegions(arkit, liveFaceFlagsRef.current.regions)

    // Procedural blink overlay on the ARKit blink channels (eyeBlinkLeft = 0,
    // eyeBlinkRight = 7) — arkit_to_mh maps these to the eye-blink controls.
    const blinkGate = liveFaceFlagsRef.current.blink ? (faceWeights?.['__blink__'] ?? 0) : 0
    if (blinkGate > 0) {
      const b = currentBlinkWeight(blinkGate)
      if (b > 0) { arkit[0] = Math.min(1, arkit[0] + b); arkit[7] = Math.min(1, arkit[7] + b) }
    }

    // ── ARKit -> the character's N-control rig, push to the splat (only on change) ──
    const rig = rigRef.current
    // ?rigconstrain=<0..1>: the SAME frame composed with neither constraint, so the
    // two stages below can be blended rather than only switched off. Null at the
    // default weight of 1, which is the shipped path unchanged — one comparison per
    // frame, no second mapping, no allocation.
    const constrainW = rigConstrain.blend
    const loose = constrainW < 1 ? looseRigRef.current : null
    // No range at all, which is exactly what `?rigclamp=none` reaches the decoders
    // with: the loose arm has to be a rig the code already produces, or an
    // intermediate weight would be a constraint neither arm applies.
    if (loose) arkitToIsaacRig(arkit, gather, loose, scratch188Ref.current, restRigRef.current, null)
    arkitToIsaacRig(arkit, gather, rig, scratch188Ref.current, restRigRef.current, rigRangeRef.current)
    // THREE STAGES, IN THIS ORDER, and the order is load-bearing.
    //
    // 1. Trained-manifold mouth mix (liveRigRegions): during live speech, pull the
    //    mouth block toward the interpolation between this bundle's trained
    //    closed/open references, with the A2F jaw picking the coordinate — the
    //    same references the panel's "Mouth open (trained)" slider drives. Live
    //    A2F frames only: clips and Rig Preview frames are authored poses.
    const flags = liveFaceFlagsRef.current
    if (liveWeight > 0 && rigWeight === 0 && liveMouthDriveRef.current && flags.mouthWeight > 0) {
      flags.lastT = applyLiveMouthMix(rig, liveMouthDriveRef.current, flags.mouthWeight)
      // The twin gets every DRIVE the constrained arm gets — this stage and the
      // blink macro below — so ?rigconstrain moves the two CONSTRAINTS and nothing
      // else. Each drive keeps its own switch (?livemouth, ?blinkmacro).
      if (loose) applyLiveMouthMix(loose, liveMouthDriveRef.current, flags.mouthWeight)
    }
    // 2. BLINK BEFORE THE PROJECTION. ARKit drives one control per eye while every
    //    trained blink row moves the whole lid group, so the raw request is off
    //    the manifold and the projection halves it (0.710 -> 0.392 measured).
    //    Driving the group makes the request a real configuration, which the
    //    projection then leaves alone (0.680). ARKit indices 0 / 7 are
    //    EyeBlinkLeft / EyeBlinkRight, and the procedural blink overlay has
    //    already been folded into them above — which is also why the region mask
    //    zeroing the A2F eye channels does not disarm this: the overlay writes
    //    after the mask, and it is the overlay that blinks during speech.
    blinkMacroRef.current?.applyInto(rig, arkit[0], arkit[7])
    if (loose) blinkMacroRef.current?.applyInto(loose, arkit[0], arkit[7])
    // 3. TRAINED SUBSPACE (ogs/rig/mouthSubspace.js), LAST, so it verifies whatever
    //    the two stages above produced. ARKit writes the controls independently,
    //    which lands the trunk's `code` latent on COMBINATIONS the model was never
    //    shown — and because geom and appr share that latent, both over-respond
    //    together: offsets overshoot and colour runs to white. Neither per-control
    //    range limiting nor a distance threshold catches it. This decomposes the
    //    rig in the basis the bundle's distinct trained poses span, clamps each
    //    coordinate to the range the data covers, and drops whatever lies outside
    //    the span. Stage 1's output is NOT automatically inside that span, and
    //    saying so was wrong: it blends a SUBSET (the mouth block) PARTWAY (0.65)
    //    toward an interpolation, which is not a convex combination of trained
    //    rows across all 168 controls. Measured, the projection still moves it
    //    ~0.42-0.53. That is the projection doing its job rather than a conflict
    //    — stage 1 lowers the distance from the manifold on every real mouth
    //    shape (COMBO-5 0.93 -> 0.53) and stage 3 removes what is left.
    mouthPcaRef.current?.projectInto(rig)
    // ?rigconstrain: everything above this line is what the two arms share, so the
    // blend is exactly "how much of the clamp + the projection lands".
    if (loose) {
      // Measured BEFORE the blend (which overwrites `rig`): how much of the face
      // the two constraints are actually moving on this frame. The readout is the
      // difference between "the slider does nothing" and "the slider is doing
      // something this pose cannot show" — during live speech the mouth block has
      // already been pulled 65% onto the trained manifold by the live mouth mix,
      // so the region you watch while it talks moves less than a static extreme
      // does. Reported on the Face rig row under the slider.
      measureConstraintDelta(rig, loose)
      blendRigInto(rig, loose, constrainW)
    }
    // 4. EYE FOLLOW (ENG-1060), LAST — deliberately after stages 1-3. On isaac-v6
    //    the trained data freezes every eye control (std 0.0000 across all 137
    //    poses), so the trained-limits clamp pins them to rest and the subspace
    //    projection's basis has no eye variance to keep — anything written
    //    earlier is erased, which is exactly what the first attempt measured
    //    (rig bit-identical with the overlay correct). Writing here trades that
    //    erasure for extrapolation on ONLY the four eye-aim controls, bounded
    //    by the DECLARED rig_range — pixel-verified acceptable on isaac under
    //    both rigs. Ticked at gate 0 too, so switching the node off fades the
    //    aim out instead of freezing it (same rule as the GLB path).
    const efSlots = eyeFollowSlotsRef.current
    if (efSlots) {
      const efGate = faceWeights?.['__eyefollow__'] ?? 0
      updateEyeFollowFromScene(avatarRoot, camera, efGate, delta, eyeFollowPivotRef.current)
      if (eyeFollowRuntime.state.weight > 0.001) {
        // eyeShare(), same as the GLB path — so the node's eyeWeight is the
        // DEFLECTION GAIN here, the knob that keeps the eye controls off the
        // declared-range rails (values the decoders never saw; at full gate
        // the extremes read as distorted lids/whites — Andres's "weird at
        // some angles"). neckWeight simply goes unspent on a splat: the face
        // board has no neck controls, and that is stated on the node.
        const overlay = eyeFollowRuntime.arkitOverlay(eyeFollowRuntime.eyeShare())
        const range = declaredRangeRef.current
        applyEyeFollowRig(rig, efSlots, overlay, range ? range[0] : -Infinity, range ? range[1] : Infinity)
      }
    }
    if (rigChanged(rig)) {
      isaacSplat.setRigDirect(rig)
      lastRigRef.current.set(rig)
      haveSentRef.current = true
    }
  }

  function rigChanged(rig) {
    if (!haveSentRef.current) return true
    const last = lastRigRef.current
    for (let i = 0; i < rig.length; i++) if (Math.abs(rig[i] - last[i]) > 1e-4) return true
    return false
  }

  // Mesh-only /ogs bundle preview (rig.glb + clip GLBs) — set by the mount probe.
  const meshPreviewRef = useRef(null)
  // The live eye branch, for the per-frame camera watch below.
  const eyesMountRef = useRef(null)
  // The head/multi-region recolor's camera watch — allocation-free, so it can ask every frame.
  const camWatchRef = useRef(null)
  if (!camWatchRef.current) camWatchRef.current = createCameraWatch()

  useFrame((_, delta) => {
    // Advance the mesh preview's mixer (no-op unless a clip is playing).
    meshPreviewRef.current?.update(delta)
    // Eye contact and the eyes' view-dependent colour both follow the CAMERA, so they are
    // driven from here rather than from a `__ORBIT_CONTROLS__` 'change' subscription: that
    // global may not exist when the branch mounts and its identity swaps on a live<->edit flip.
    // Cheap — syncCamera() returns without work unless the camera actually moved. Ahead of the
    // phase gate on purpose: the eyes own their own SplatMesh, so they do not wait on the
    // head's renderer chain being wired.
    eyesMountRef.current?.syncCamera()
    // Only drive the splat while the renderer chain is fully connected. No
    // renderer node (or an unwired one) → nothing to render, so we don't even
    // evaluate the face graph or advance the audio buffer.
    if (checkPhase() !== 'rendering') return
    // Camera-only recolor: appr is conditioned on a plücker ray, so its colours are a
    // function of the view and a camera move has to re-run it. Driven from HERE rather than
    // from a `__ORBIT_CONTROLS__` 'change' subscription for the same reason as the eyes above:
    // that global's identity SWAPS when the camera rig takes over in live mode (drei
    // OrbitControls → cameraRigCompat, Scene.jsx), so a listener bound once at pipeline-ready
    // ends up on an object nobody dispatches on — the camera kept moving and the splat colour
    // silently froze, in live mode only. The watch compares the live camera, so it cannot go
    // stale; runAppearance() is a no-op before an engine attaches and every engine coalesces
    // re-entrancy, so asking per frame is cheap. Behind the phase gate: a hidden splat has
    // nothing to recolour.
    // In the AVATAR's frame: the root turning under a still camera is a view change too.
    if (camWatchRef.current.movedLocal(watchCam.update(camera, avatarRoot))) isaacSplat.runAppearance()
    // Advance the A2F frame buffer so getCurrentArkit() tracks the live
    // Convorcher stream. HeadModel normally does this (via applyToMeshes) but
    // it isn't mounted in splat mode.
    animationBuffer?.advancePlayback()
    driveSplatFace(delta)
  })

  useEffect(() => {
    if (!gl || !scene || !camera) return
    let cancelled = false
    let updater = null
    // ?meshview's branch meshes — torn down with everything else, since they hold
    // a geometry per branch and outlive nothing.
    let meshOverlay = null
    let eyesMount = null
    let ownedSpark = null
    let avatarPrevVisible = null
    // Every live OGSBranch (single-region face, head+teeth, myra) — teardown
    // disposes their sessions, gpu-buffer tensors, plücker and lifter.
    let liveBranches = null
    let unregisterFramer = null
    let booted = false
    let spark = null
    let initStarted = false

    // ENG-991: turn a boot that can NEVER become ready — a blueprint whose
    // renderer chain is missing ('three') or unwired ('created'), the state
    // that left eyeline on an eternal "Loading avatar…" — into a labeled
    // App-level diagnostic instead of a console.info. Debounced inside the
    // detector (the phase is legitimately blocked for a moment while the DB
    // blueprint is still in flight); polled on a plain interval, NOT useFrame,
    // so a stuck boot is detected even if the render loop is idle. 10s clears
    // the slowest observed staging blueprint fetch (~5s) with margin, and a
    // false fire self-heals: the chain arriving flips 'unblocked' live.
    const blockDetector = createRenderBlockDetector({ delayMs: 10_000 })
    const renderBlockedMessage = () => (
      phaseRef.current === 'created'
        ? 'The Face Blueprint has an OGS Renderer node, but its input chain is broken or disabled — nothing can render this splat character. Rewire the OGS Branch → Renderer chain in the Face Blueprint, or restore an earlier blueprint version.'
        : 'The Face Blueprint has no OGS Renderer node — nothing can render this splat character. Its blueprint was likely saved empty or overwritten; restore an earlier blueprint version, or re-add the OGS Branch + Renderer chain in the Face Blueprint.'
    )
    const blockPoll = setInterval(() => {
      if (cancelled || initStarted) return
      if (blockDetector.poll(Date.now()) === 'blocked') {
        console.error(`[ogs] render blocked: ${renderBlockedMessage()}`)
        onRenderBlocked?.(renderBlockedMessage())
      }
    }, 1000)

    // Mesh-only /ogs bundle (rig.glb + clip GLBs, no inference assets): render the
    // plain skinned GLB — no Spark, no ONNX, and no blueprint renderer gate (a bare
    // mesh is the 'three' phase, not an OGS pipeline). Probe is a single list call.
    const fetchOgsBytes = async (url) => {
      const res = await fetchAamFile(url)
      if (!res.ok) throw new Error(`/ogs fetch ${url}: HTTP ${res.status}`)
      return res.arrayBuffer()
    }
    const startMeshPreview = async (sel) => {
      const preview = new MeshClipPreview()
      await preview.load(sel, fetchOgsBytes)
      if (cancelled) { preview.dispose(); return }
      preview.mount(scene)
      meshPreviewRef.current = preview
      window.__OGS_MESH_PREVIEW__ = preview
      frameCameraToBox(camera, preview.worldBox())
      console.info(`[mesh-preview] rig mounted; clips: ${preview.clipNames().join(', ') || '(none)'}`)
      onReady?.()
    }
    probeMeshOnlyBundle(resolveCharacterId())
      .then((sel) => { if (sel && !cancelled) return startMeshPreview(sel) })
      .catch((e) => console.error('[mesh-preview] failed:', e))


    // Phase 'created' — create the SparkRenderer (find-or-create; idempotent).
    // "Splat renderer created, idle": it exists but draws nothing until the
    // pipeline starts. Ownership is tracked so teardown disposes only ours.
    const ensureSpark = () => {
      if (spark) return
      const info = findOrCreateSpark(scene, gl)
      spark = info.spark
      if (info.owned) ownedSpark = info.spark
      console.info('[isaac] splat renderer created — idle (renderer node present, inputs not connected)')
    }

    // The GPU skinners currently holding device buffers, so the character switch below can
    // free them — nothing else has a handle on the drive once the registry is reset.
    let livePartSkinners = null
    // The live branches' own body skinners, disposed with the rest — they hold a bind
    // group over the lifter's vertex buffer, which the next character's lifter does not own.
    const liveBodySkinners = new Map()

    // One GPU skinner per branch the part rig skins, keyed by branch name. Empty when
    // there is no device, no GPU lifter, or the branch's shader output does not match
    // what the CPU implementation produces at the same pose — every one of those keeps
    // the CPU path rather than rendering a body nobody can tell is wrong.
    // Skin a LIVE branch by the body rig, exactly as every other part is skinned.
    //
    // A live branch owns its vertices — they are its deform's output — which is why the
    // body normally reaches it as one rigid delta on its anchor. One delta is right for a
    // head and wrong for the neck and shoulder cap a MetaHuman `head_lod0` carries below
    // it, and no rigid transform can be right for both. It does not have to be: such a
    // branch also has a part in rig.glb, so the deform can give the expression and the
    // skinning the carry, which is how a face rig and a body rig always compose. The
    // skinner reads the lifter's own vertex buffer and unplaces it into the part's bind
    // space first (wgsl/part_unplace.wgsl); the result goes back as the lifter's verts.
    //
    // Returns the branches that took it. Nothing is silent: a branch that cannot be
    // skinned says why and falls back to the two-joint anchor blend.
    const installLiveBodySkin = async ({ driver, branches, isLive, device }) => {
      const done = new Map()
      if (!device || ogsGpuFlags().meshView !== 'off') return done
      for (const br of branches) {
        if (!isLive(br)) continue
        const plan = driver.verts.skinPlan?.(br.name)
        const lifter = br.lifter
        if (!plan || !lifter?.vertsBuffer || typeof lifter.setVertsGpu !== 'function') {
          console.info(`[ogs] '${br.name}': the body cannot skin this branch `
            + `(${!plan ? 'no part in rig.glb' : 'this lift has no GPU vertex buffer'}) — anchored instead`)
          continue
        }
        let skinner = null
        try {
          skinner = createGpuPartSkinner(device, plan)
          if (!skinner) throw new Error('the plan carries no vertices')
          skinner.setSourceVerts(lifter.vertsBuffer)
          // Parity, at the one pose where the answer is known: the rig is at rest here and
          // the lifter holds the branch's neutral, so skinning it must give the neutral
          // back — linear blend skinning is the identity at the bind pose, which is the
          // same statement as the part fit's own "neutral mean=0.0mm". A transposed or
          // mis-scaled inverse in the unplace pass fails here rather than as a subtly
          // wrong head nobody can see is wrong.
          const delta = await skinner.verifyIdentity()
          if (!(delta <= MAX_SKIN_DISAGREEMENT_METRES)) {
            throw new Error(`skinning at the rest pose moves the branch `
              + `${delta == null ? 'an unknown amount' : `${(delta * 1000).toFixed(2)} mm`}`)
          }
          br.bodySkin = () => { const out = skinner.skin(); if (out) lifter.setVertsGpu(out) }
          // setVertsGpu runs the jacobians on the SKINNED verts, so the pose's own pass
          // would only compute them for vertices nothing ever lifts.
          lifter.deferJacobians = true
          done.set(br.name, skinner)
          liveBodySkinners.set(br.name, skinner)
        } catch (e) {
          skinner?.dispose()
          br.bodySkin = null
          console.error(`[ogs] '${br.name}': body skinning refused (${e?.message ?? e}) — anchored instead`)
        }
      }
      if (done.size) console.info(`[ogs] body-skinned live branches: ${[...done.keys()].join(', ')}`)
      return done
    }

    const buildPartSkinners = async ({ driver, branches, isLive, device }) => {
      const skinners = new Map()
      livePartSkinners = skinners
      // Same gate the exact rig's deform has, for the same reason: the mesh view draws
      // `branch.verts`, and a GPU-skinned branch never writes them — the posed vertices go
      // straight to the lifter's buffer and that array keeps its neutral pose. Left on, a
      // mesh view would draw a NEUTRAL body beside walking splats and look like proof of a
      // bug that is really the overlay reading the wrong array.
      if (ogsGpuFlags().meshView !== 'off') {
        console.info('[ogs] part skin stays on the CPU — ?meshview draws branch.verts, which only the CPU path writes')
        return skinners
      }
      if (!device) {
        // The CPU lifter's own path: there is no device to skin on, and the verts have to
        // reach the CPU anyway. Said out loud because it is the difference between a walk
        // at 30 Hz and one at 8.
        console.info('[ogs] part skin stays on the CPU — the lift has no GPU device')
        return skinners
      }
      const deltas = []
      for (const br of branches) {
        if (isLive(br) || !driver.verts.branches[br.name]) continue
        if (typeof br.lifter?.setVertsGpu !== 'function') continue
        let skinner = null
        try {
          skinner = createGpuPartSkinner(device, driver.verts.skinPlan(br.name))
          // The CPU answer at the CURRENT (rest) pose is the reference. deform() writes
          // the branch's own buffer, which nothing has read yet at build time.
          const delta = await verifyGpuSkinner(skinner, driver.verts.deform(br.name))
          if (delta == null || !(delta <= MAX_SKIN_DISAGREEMENT_METRES)) {
            throw new Error(`disagrees with the CPU skinner by ${delta == null ? 'an unknown amount' : `${(delta * 1000).toFixed(2)} mm`}`)
          }
          deltas.push(delta)
          skinners.set(br.name, skinner)
        } catch (e) {
          skinner?.dispose()
          console.error(`[ogs] branch '${br.name}': GPU skinning refused (${e?.message ?? e}) — this branch stays on the CPU`)
        }
      }
      if (skinners.size) {
        console.info(`[ogs] part skin on GPU: ${[...skinners.keys()].join(', ')} `
          + `(max ${(Math.max(...deltas) * 1000).toFixed(3)} mm from the CPU skinner)`)
      } else {
        console.info(`[ogs] part skin: no branch took the GPU path (${branches.map((br) => `${br.name}:`
          + `${isLive(br) ? 'live' : ''}${driver.verts.branches[br.name] ? '' : 'nopart'}`
          + `${typeof br.lifter?.setVertsGpu === 'function' ? '' : 'nolifter'}`).join(' ')})`)
      }
      return skinners
    }

    // The bundle's part rig (rig.glb), registered LAZILY for whoever poses the body —
    // the body blueprint, a clip preview, a generated walk (partDriveRegistry). Shared
    // by both pipelines: the rig, its per-branch fit and the frame it renders through are
    // properties of the BUNDLE, and which pipeline decodes the appearance changes none of
    // them. What each pipeline supplies is how a posed frame reaches the screen
    // (`present`) and which branches own their own verts (`isLive` — the neutral pipeline
    // has none, the live one has its face).
    const registerPartDrive = ({ packs, scn, branches, worldRotation, worldScale, isCancelled, isLive, present, device, onPresent }) => {
      const rigUrl = partRigUrl((name) => !!packs.getBytes(name), packs.texBase)
      if (!rigUrl) {
        console.info(`[ogs] no ${PART_DRIVER_RIG} in this bundle — no body rig to pose`)
        return
      }
      const buildDriver = async () => {
        const driver = await loadPartDriver({
          rigUrl,
          branchMeshes: Object.fromEntries(scn.branches.map((b) => [b.name, b.mesh])),
        })
        if (isCancelled()) { driver.rig.dispose(); return }
        // The rotation the rig's own space is RENDERED through — see the phase-3 copy.
        driver.rigToWorld = rigToWorldRotation(driver.verts, worldRotation)
        // And its SCALE: this skeleton is centimetres, the clip that drives it is metres.
        driver.rigToWorldScale = rigToWorldScale(worldScale)
        window.__OGS_DRIVER__ = driver
        for (const branch of driver.fit) console.info(`[ogs] ${formatPartDriverFit(branch)}`)
        // Own buffer per rigid rider — neutralVertices must never be mutated, and the
        // no-per-frame-alloc rule means it cannot be made inside driveAt.
        const rigidVerts = new Map(branches
          .filter((br) => !driver.verts.branches[br.name] && !isLive(br))
          .map((br) => [br.name, new Float32Array(br.neutralVertices.length)]))
        // Skinning on the GPU, where the jacobians and the lift already are. It is the
        // one per-vertex stage that ran on the CPU (58 ms a frame here), so it is what
        // held a walk to ~12 Hz. Verified against the CPU implementation at THIS pose
        // before it is trusted — one formula, two implementations, checked once.
        const skinners = await buildPartSkinners({ driver, branches, isLive, device })
        // A live branch rides ONE bone, and a MetaHuman `head_lod0` carries more than a
        // head: the neck, and a cap of the shoulders. Those vertices belong to the spine,
        // so the head's full rotation hinges them out of the character's clothes at the
        // collar. The blend mixes a SECOND anchor in per vertex, weighted by the part
        // rig's own skinning — the fact the body already acts on.
        await installLiveBodySkin({ driver, branches, isLive, device })
        let reported = null
        // The clip time the last pose actually REACHED THE SCREEN at — the root is placed
        // at this, not at the caller's clock (multipartBodyDrive's `posedTime`).
        //
        // Two times, because they are genuinely different moments. `applied` is set when
        // the pose callback runs, which `runPosed` does SYNCHRONOUSLY at the top of a pass;
        // the present is at the BOTTOM of that same pass. Promoting at `applied` (or after
        // `await present(...)`, which resolves immediately when the engine coalesces the
        // pose) reports a frame that is not on screen yet, and the driver re-reads it later
        // in the same tick — so the root is placed one whole pass ahead of the legs, by a
        // lead that grows and shrinks with how busy the face decode is. `onPresent` fires
        // after the draw, which is the only moment "landed" is true.
        let posedTime = null
        let appliedTime = null
        onPresent?.(() => { if (appliedTime !== null) { posedTime = appliedTime; appliedTime = null } })
        const driveAt = async (t) => {
          // `null` is multipartBodyDrive saying NOBODY poses the rig this frame. The myra
          // pipeline uses that slot to run a pending recolour; this one has no separate
          // recolour to run, so it is simply a no-op — but it must not reach `setTime`,
          // which would set every mixer to a null time and pose the whole body at NaN,
          // and the rig does not recover from it.
          if (t == null) return false
          if (isCancelled() || !gateActiveRef.current) return false
          driver.rig.setTime(t)
          await present(() => {
            const report = applyPartRigPose({
              branches,
              verts: driver.verts,
              headDelta: driver.rig.headDelta(driver.verts.fitForPart?.(driver.rig.headRestPart)),
              rigidVerts,
              isLive,
              skinners,
            })
            // Applied, NOT yet drawn — promoted by the onPresent hook above. A pose
            // superseded by a newer one never runs this callback at all, so its time is
            // never claimed, which is what keeps the two honest.
            appliedTime = t
            if (!reported) {
              reported = report
              console.info(`[ogs] part drive posing — ${formatPartRigPose(report)}`)
            }
            // The live branches are the ones whose anchor moved; the engine re-poses them.
            return branches.filter((br) => isLive(br))
          })
        }
        const duration = driver.rig.clip?.duration ?? null
        console.info(`[ogs] part drive ready (${driver.rig.parts.length} parts, `
          + `${duration ? `${duration.toFixed(2)}s clip` : 'no clip yet'})`)
        return { driveAt, duration, rig: driver.rig, rigToWorld: driver.rigToWorld, rigToWorldScale: driver.rigToWorldScale, posedTime: () => posedTime }
      }
      // The rig is registered, not a clip: whoever poses the body builds it. Never after
      // teardown — a late registration would hand the next character a builder over this
      // disposed scene.
      if (isCancelled()) return
      partDriveRegistry.register({ build: buildDriver })
    }

    // Live head+teeth pipeline (isaac V2, scene.body_rig === 'head'): both
    // regions consume the SAME live control vector, per-branch deform drives
    // novel geometry, and the blueprint's plugin.ogs.branch nodes decide which
    // regions render. This is the LIVE-RIG starter: it re-decodes on every rig
    // push, which is why the single-region adapter routes here (body_rig:'head').
    const startHeadTeethPipeline = async (packs, scn, providers) => {
      const flags = ogsGpuFlags()
      const WORLD_SCALE = scn.manifest.worldScale // isaac V2 trains in cm → 0.01
      const ORIGIN_SCALE = 1 / WORLD_SCALE
      const WORLD_OFFSET = scn.manifest.worldOffset
      // The training frame's own orientation, undone on the SplatMesh and inverted
      // onto the plücker camera — everything the decoders see stays pre-rotation
      // (render/worldRotation.ts). ?worldrot=x,y,z overrides the bundle.
      // Before anything reads the eye-region flags: the bundle gets to decline a
      // drive its capture cannot take, and both pipelines ask the same way.
      applyBundleFaceDrives(scn.manifest)
      const WORLD_ROTATION_DEG = resolveWorldRotation(scn.manifest.worldRotation)
      const WORLD_ROTATION = worldRotationQuaternion(WORLD_ROTATION_DEG)
      // Bundle-declared plücker conditioning (isaac V2 ships moment_scale 0 =
      // direction-only, the "black lines" fix); ?moment=<v> A/B override wins.
      const momentScale = applyMomentScale(scn.manifest.momentScale)
      console.info(`[ogs-ht] momentScale=${momentScale} worldScale=${WORLD_SCALE} worldOffset=[${WORLD_OFFSET.join(', ')}] worldRotation=[${WORLD_ROTATION_DEG.join(', ')}]`)
      // Decoder precision picked on the OGS Avatar node (fp16 = 16-bit default);
      // the ?fp16 / ?geomfp16 / ?apprfp16 kill-switches still win inside the branch.
      const precision = readAvatarPrecision(faceGraphState.nodes)
      const preferFp16 = precisionPrefersFp16(precision)

      // ORDER IS LOAD-BEARING (gpuDevice.ts): prepare() installs the shareable
      // adapter and MUST run before the first session create — after that ORT
      // has cached its device; resolve() reads the device ORT actually built and
      // therefore MUST run after. Everything downstream (gpu-buffer io, plücker,
      // lifter) hangs off that one decision, so decoders and lifter can never
      // disagree about which device a GPUBuffer belongs to.
      const prepared = await prepareOgsLiftDevice(ogsOrt, providers, flags)
      if (cancelled) return

      // isaac / eyeline take the shared lifter; ?myralift=1 is this path's only route to the fork.
      const liftChoice = chooseLift({ subject: scn.manifest.subject, character: resolveCharacterId() }, flags.myraLift)
      console.info(`[ogs-ht] ${liftChoice.impl} lift (${liftChoice.reason})`)

      const branches = scn.branches.map((b, i) => new OGSBranch({
        id: `branch_${b.name}`,
        name: b.name,
        members: [`branch_${b.name}.geom`, `branch_${b.name}.appr`],
        // The renderer's input-port pair for this region — the same names
        // plugins/ogs-renderer/plugin.json declares (graph/branchPorts.ts).
        ports: branchPortNames(b.name, i),
        live: {
          assets: b,
          providers,
          getBytes: packs.getBytes,
          fetchBytes: packs.fetchBytes,
          preferFp16,
          originScale: ORIGIN_SCALE,
          worldOffset: WORLD_OFFSET,
          worldRotation: WORLD_ROTATION,
          onDowngrade: reportDowngrade,
          flags,
          lift: liftChoice.impl,
          // Only the exact rig reads the control names — they are mapped onto
          // the DNA's GUI controls, and the rig itself comes out of the mounted
          // orl_* character assets. The plan says WHICH rig the loader fetched.
          rigNames: packs.rigNames,
          rigPlan: packs.rigPlan,
        },
      }))
      // Publish BEFORE any session is built: teardown only releases what it can
      // see, so a cancel mid-init (character switch during a 300 MB load) would
      // otherwise leak every decoder session it had already created.
      liveBranches = branches
      // Debug seam: the live OGSBranch instances, so a console/test can read a
      // branch's lifter output (positions/scales/opacities/colors) and compare it
      // against a reference implementation. Read-only; never on the render path.
      window.__OGS_BRANCHES__ = branches
      const ctxFor = (br, sharedDevice) => ({
        ort: ogsOrt, uvRes: br.uvRes, rigDim: br.rigDim, sharedDevice, gpuBufferIo: prepared.shared,
      })
      // Phase 1 — sessions only (ORT's device doesn't exist yet).
      for (const br of branches) {
        await br.init(ctxFor(br, null))
        if (cancelled) return
      }
      const lift = await resolveOgsLiftDevice(ogsOrt, prepared)
      if (cancelled) return
      // ORT built a device the lifter can't share (dual-GPU powerPreference is
      // best-effort): the gpu-buffer hints are baked in at create time, so the
      // only repair is to download every output. Loud, never silent.
      if (lift.demoted) for (const br of branches) br.setDownloadOutputs(true)
      const sharedDevice = lift.mode === 'shared' ? lift.device : null
      const ioMode = prepared.shared && !lift.demoted ? 'gpu-buffer' : 'cpu'
      // Phase 2 — plücker + lifter on the resolved device.
      for (const br of branches) {
        await br.initGpu(ctxFor(br, sharedDevice), lift.device)
        if (cancelled) return
        console.info(`[ogs-ht] branch '${br.name}' ready: V=${br.neutralVertices.length / 3} uv=${br.uvRes} rigDim=${br.rigDim} deform=${br.deformKind} lift=${br.liftKind()} plucker=${br.pluckerKind()} io=${ioMode} geom=${br.geomUsedFp16 ? 'fp16' : 'fp32'} appr=${br.apprUsedFp16 ? 'fp16' : 'fp32'}`)
      }
      liveBranches = branches
      reportLift('ogs-ht', liftChoice, branches)

      // Eye Follow (ENG-1060): this is THE live-rig pipeline, so the rig route
      // is available whenever the control space carries the eye-aim controls.
      // The aim ORIGIN is the eye midpoint from branch geometry (checkpoint cm),
      // mapped through the same placement SplatMeshUpdater applies to the mesh
      // (world = R·(p·scale) + offset) into avatar-root-local space — the root
      // is what updateEyeFollowFromScene takes, so a gizmo-moved avatar keeps
      // its aim origin.
      // ?gaze=0 stands the eye aim down for this run. Done HERE rather than at the
      // per-frame write so the node REPORTS it: a drive that silently stops writing
      // reads as a broken rig, which is the same rule the two branches below follow.
      if (eyeFollowSlotsRef.current && !liveFaceFlagsRef.current.gaze) {
        eyeFollowSlotsRef.current = null
        console.info(liveFaceFlagsRef.current.source?.gaze === 'bundle'
          ? '[ogs-ht] eye follow: this bundle declares face_drives.gaze off — the eye aim is not driving this run'
          : '[ogs-ht] eye follow: ?gaze=0 — the eye aim is not driving this run')
      }
      if (eyeFollowSlotsRef.current) {
        const pivotCk = splatEyePivot(branches.map((br) => ({ name: br.name, verts: br.neutralVertices })))
        if (pivotCk) {
          const p = new THREE.Vector3(pivotCk[0], pivotCk[1], pivotCk[2]).multiplyScalar(WORLD_SCALE)
          if (WORLD_ROTATION) p.applyQuaternion(WORLD_ROTATION) // null when the bundle declares none
          if (WORLD_OFFSET) p.add(new THREE.Vector3(WORLD_OFFSET[0], WORLD_OFFSET[1], WORLD_OFFSET[2]))
          eyeFollowPivotRef.current = [p.x, p.y, p.z]
        }
        setSplatEyeFollowCapability({ route: 'rig' })
        console.info(`[ogs-ht] eye follow: rig route on ${eyeFollowSlotsRef.current.slots.length} eye controls, `
          + `pivot ${eyeFollowPivotRef.current ? eyeFollowPivotRef.current.map((v) => v.toFixed(3)).join(', ') : 'none (root)'}`)
      } else {
        setSplatEyeFollowCapability({
          route: 'none',
          reason: liveFaceFlagsRef.current.gaze
            ? 'Not supported on this bundle — its control space (rig_names.json) has no eye-aim controls (CTRL_L/R_eye) for the aim to drive.'
            : liveFaceFlagsRef.current.source?.gaze === 'bundle'
              ? 'Declared off by this bundle (scene.json face_drives.gaze) — its pose table never contained the eye-aim combinations. ?gaze=1 drives it anyway.'
              : 'Stood down by ?gaze=0 for this run — drop the flag to drive the eye aim again.',
        })
      }

      const headBranch = branches.find((b) => b.name === 'head') ?? branches[0]
      // The DNA's RAW control layer for the Rig Preview readout. Set HERE and
      // not at registerRigBoard: the board is registered from startPipeline
      // before this runs, so no branch exists to ask yet. Read off the HEAD
      // branch — it is the one a head.dna bakes and the only one carrying a
      // RigLogic instance; null on the rig2mesh path, which is the default.
      setRigBoardRawLayer(headBranch?.rawLayer ?? null)
      // Publish the head branch's resolved precision (the dominant cost) so the
      // OGS Avatar node shows what actually built, after any auto-downgrade.
      const reportHtPrecision = (requested) => reportPrecision({
        requested,
        geom: headBranch.geomUsedFp16 ? 'fp16' : 'fp32',
        appr: headBranch.apprUsedFp16 ? 'fp16' : 'fp32',
      })
      reportHtPrecision(precision)

      ensureSpark()
      updater = new SplatMeshUpdater(avatarRoot)
      updater.meshScale = WORLD_SCALE
      updater.meshOffset = WORLD_OFFSET
      updater.meshRotation = WORLD_ROTATION

      const avatar = window.__AVATAR_SCENE__
      if (avatar) { avatarPrevVisible = avatar.visible; avatar.visible = false }

      // Frame the camera BEFORE the first appearance pass — appr's plücker is
      // view-dependent and a degenerate boot camera corrupts the colors.
      // Also the Frame Avatar / F command's implementation for this character:
      // the splat extent lives in these branches, not in a rig scene graph.
      const frameToBranches = () => {
        const V = branches.reduce((n, b) => n + b.neutralVertices.length, 0)
        if (!V) return false
        const pos = new Float32Array(V)
        let off = 0
        for (const b of branches) { pos.set(b.neutralVertices, off); off += b.neutralVertices.length }
        return frameCameraToSplats(camera, { out: { positions: pos, opacities: new Float32Array(V / 3), count: V / 3 } }, WORLD_SCALE, WORLD_OFFSET, { preserveDirection: booted, worldRotation: WORLD_ROTATION })
      }
      frameToBranches()
      booted = true
      unregisterFramer = setContentFramer(frameToBranches)

      // ONE terminal render node for BOTH branches: it lifts every source that
      // produced UVs this pass and presents the concatenated set once, so a rig
      // update costs ONE present (the readbacks are still one per branch, see
      // OGSRenderer's header). Each source reserves a fixed
      // packed-slot range (uv_res²) for the whole session — a region toggle
      // zeroes its slots rather than shifting everyone else's and tearing
      // Spark's async depth sort.
      const ogsRenderer = ogsRendererPlugin.create({
        id: 'renderer',
        updater,
        sources: branches.map((br) => ({
          name: br.name,
          ports: br.outputs.map((p) => p.name),
          lifter: br.lifter,
          getVerts: () => br.verts,
          capacity: br.uvRes * br.uvRes,
        })),
      })
      await ogsRenderer.init(ctxFor(headBranch, sharedDevice))
      if (cancelled) { updater.dispose(); return }

      // ?meshview — the branch GEOMETRY, drawn from the posed vertices the lift
      // anchors to. Mounted before the engine so the first decode's pose lands on
      // it, and updated from onPresented (a pose cadence, not a frame one).
      if (flags.meshView !== 'off') {
        meshOverlay = createBranchMeshOverlay(avatarRoot, branches, {
          scale: WORLD_SCALE, offset: WORLD_OFFSET, rotation: WORLD_ROTATION,
          wireframe: flags.meshView !== 'solid',
        })
        if (!meshOverlay) {
          console.error('[ogs-ht] ?meshview: no branch carries a face list — nothing to draw')
        } else {
          console.info(`[ogs-ht] ?meshview=${flags.meshView} — drawing ${meshOverlay.count} branch mesh(es)`
            + `${flags.meshView === 'both' ? ' over the splats' : ', splats hidden'} (CPU deform forced)`)
        }
      }
      const presentedHooks = []
      const engine = new HeadTeethEngine({
        branches,
        // Local-frame camera, re-resolved per decode: appearance is
        // view-dependent, so transforming the avatar must not shift its shading
        // (ENG-895). See ogs/render/localFrameCamera.
        camera: () => localCam.update(camera, avatarRoot),
        renderer: ogsRenderer,
        onPresented: (count) => {
          // Whoever needs to know that a pose is now ON SCREEN, rather than merely
          // applied — the part drive promotes its landed time here, and the root is
          // placed at that (multipartBodyDrive: the root never leads the legs).
          for (const fn of presentedHooks) fn()
          // The mesh follows the same pose the present just drew, so the two
          // views can never disagree about which frame they are showing.
          meshOverlay?.update()
          window.__ISAAC_SPLAT_COUNT__ = () => count
          window.__OGS_HEAD_TEETH__ = {
            regions: { ...engine.active }, count,
            worldScale: WORLD_SCALE, worldOffset: WORLD_OFFSET,
            momentScale, rigDim: engine.rigDim,
          }
        },
        isActive: () => !cancelled && gateActiveRef.current,
      })
      const poseTable = await resolveRestRig(packs, packs.scene)
      const restRig = poseTable?.rest ?? null
      if (cancelled) return
      // TWO DIFFERENT RESTS, and conflating them is what left the live mouth unable
      // to close. `restRig` (pose-table row 0) is a GEOMETRY fact — the pose whose
      // baked mesh IS `neutral_vertices` — and calibrate() must anchor there. The
      // pose the ARKit deltas compose onto only has to LOOK like a face at rest,
      // and row 0's mouth is slightly open (jaw 0.173, closed pool 0.003-0.063).
      // Since arkitToMh emits no negative mouth deltas, that made row 0's aperture
      // a FLOOR the live face could never get under: MouthClose moved nothing.
      // The donor row is used WHOLESALE, not grafted onto row 0. graftClosedMouth
      // already finds the right row — the closed-pool pose whose MOUTH is nearest
      // row 0's — and taking the whole thing keeps the base a REAL captured frame
      // rather than a chimera of two. Rendered side by side, row 85 is a clean
      // neutral (closed lips, level gaze) and is on-manifold by construction
      // (distance 0.00 to a trained pose, against 0.68 for the graft).
      //
      // The alternative donor rules were checked and both are worse: the closed row
      // nearest the trained MEAN (row 147) is a mid-blink frame with the lids down
      // and the gaze dropped, and assembling mouth/eyes/rest from three different
      // donors lands 1.82 from anything trained — further off-manifold than the
      // combinations that make the decoders over-respond in the first place.
      // Row count, derived — resolveRestRig hands back {rest, presets, dim} and
      // the table is dense, so this is the one place that needs to say it.
      const poseRows = poseTable ? poseTable.presets.length / poseTable.dim : 0
      const graft = poseTable && restRig
        ? graftClosedMouth({
          spec: parseMouthController(packs.scene?.mouth_controller),
          pose: restRig, presets: poseTable.presets, dim: poseTable.dim,
        })
        : null
      const arkitBase = graft
        ? poseRow(poseTable.presets, poseTable.dim, graft.row)
        : restRig
      if (graft) {
        console.info(`[ogs-ht] ARKit base: trained pose-table row ${graft.row} `
          + '(the closed-mouth pose whose mouth is nearest row 0); the geometry anchor stays row 0')
      }
      restRigRef.current = arkitBase
      // The SAME resolver the rig board's sliders clamp against — a bundle that
      // declares no rig_range is a [0, 1] MetaHuman board to both, so one UI can
      // no longer blow up a pose the other clamps (see resolveRigRange).
      const declaredRange = resolveRigRange(packs.scene)
      // TRAINED-DOMAIN LIMITS ON THE ARKIT PATH TOO. The raw rig board has clamped
      // its sliders to these since ENG-946 — a control driven across its full
      // declared rig_range leaves the training distribution and the decoders
      // extrapolate into artifacts. The ARKit path clamped only to that declared
      // range, so the SAME pose blew up or did not depending on which UI produced
      // it: an ARKit slider at 1.0 can sit 5-7x past a control's trained maximum
      // (see arkitToIsaacRig). Same `trainedLimits`, so the two paths agree by
      // construction rather than by two people remembering the same rule.
      // The projection does NOT subsume this. It removes the out-of-SPAN part of a
      // rig, but a point inside the span can still leave an individual control's own
      // trained [min,max] — different sets. Measured on eyeline-v10c AFTER the region
      // projection: dropping this clamp takes the worst overshoot across the 52 single
      // ARKit channels from 0.104 to 0.348, and MouthRollUpper (a reported breaker)
      // from 0.001 to 0.098. So it is ON by default and the A/B is a CHOICE
      // (?rigclamp=trained|declared|none), not an on/off — "off" is two different
      // things and the old `?rigclamp=0` never said which. ?rigclamppad widens the
      // trained box instead, which is the knob a narrow capture actually needs: on a
      // 30-pose bundle the unpadded box keeps only 40% of the median ARKit channel's
      // travel, and the amplitude has to come from somewhere that is still measured.
      const wantClamp = flags.rigClamp
      const limits = wantClamp === 'trained' && declaredRange && poseTable
        ? trainedLimits(poseTable.presets, poseTable.dim, declaredRange,
                        flags.rigClampPad ?? undefined)
        : null
      // `none` is the only value that reaches the decoders unclamped, and it says so:
      // a run made with it is a diagnostic, not the avatar.
      rigRangeRef.current = wantClamp === 'none' ? null : (limits ?? declaredRange)
      if (wantClamp !== 'trained') {
        console.warn(`[ogs-ht] ?rigclamp=${wantClamp} — the live ARKit path is clamped to `
          + `${wantClamp === 'declared' ? `the declared range [${declaredRange.join(', ')}]` : 'NOTHING'}, `
          + 'not the trained box. A control can now sit multiples past anything the decoders saw.')
      } else if (flags.rigClampPad != null) {
        console.info(`[ogs-ht] ?rigclamppad=${flags.rigClampPad} — trained box widened by that much per side`)
      }
      // ?rigconstrain=<0..1> — the BLEND between the unconstrained rig and the
      // constrained one, rather than a third switch beside ?rigclamp / ?rigpca.
      // Both of those can only take their stage all the way off, and on a narrow
      // capture the setting a character wants is between: the clamp alone keeps
      // 40% of the median ARKit channel on a 30-pose bundle, while none of it
      // over-responds. Blending the two RESULTS means every value is a point on
      // the segment between two rigs the pipeline already produces.
      // Armed from the flag here and editable from the Rig Preview panel after —
      // a character switch re-arms rather than inheriting the previous
      // character's panel setting, whose bundle had a different pose table.
      armRigConstrain(flags.rigConstrain)
      // LIVE getters: `Object.assign` would freeze the readout at load, and this
      // value is the one the panel changes. `set` is the console seam onto the
      // same setter the panel calls, so a spec drives production code.
      window.__RIG_CONSTRAIN__ = {
        get blend() { return rigConstrain.blend },
        get source() { return rigConstrain.source },
        get initial() { return rigConstrain.initial },
        get available() { return rigConstrain.available },
        // What the constraint is moving on the last composed frame — null at blend
        // 1, where no twin is composed to compare against.
        get delta() { return rigConstrain.delta },
        set: (v) => setRigConstrain(v, 'console'),
      }
      if (flags.rigConstrain < 1) {
        console.warn(`[ogs-ht] ?rigconstrain=${flags.rigConstrain} — only that fraction of the trained `
          + `clamp + subspace projection reaches the rig${flags.rigConstrain === 0
            ? ' (i.e. NOTHING: the same rig as ?rigclamp=none&rigpca=off)'
            : ''}. The drives (?livemouth, ?blinkmacro, ?regionmask) are unaffected.`)
      }
      // Eye Follow clamps to the DECLARED range, never the trained box — the
      // trained box has zero width on isaac's eye controls (see eyeFollowRig.js).
      declaredRangeRef.current = declaredRange
      // Eye Follow TRAINED drive: a bundle whose capture drove gaze through the
      // rig (eyeline: C_eye spans its full range across the pose table) barely
      // answers single controls — the decoders respond along trained correlated
      // CONFIGURATIONS, so the aim interpolates the eye region toward
      // base-conditioned trained reference rows, one per direction. A frozen
      // label (isaac) returns null and the direct measured convention stays.
      // Anchored on arkitBase — the pose the live path composes onto — so aim 0
      // is exactly a no-op. See buildGazeDrive's header for the measurements.
      if (eyeFollowSlotsRef.current && poseTable) {
        const drive = buildGazeDrive(
          packs.rigNames, poseTable.presets, poseTable.dim,
          packs.scene?.mouth_controller?.eyes_idx, arkitBase)
        if (drive) {
          eyeFollowSlotsRef.current = drive
          const r = drive.refs
          console.info(`[ogs-ht] eye follow: trained gaze drive — refs L=${r.yawPos ?? '—'} R=${r.yawNeg ?? '—'} `
            + `up=${r.pitchPos ?? '—'} down=${r.pitchNeg ?? '—'} (${drive.slots.length} eye-region deltas; `
            + 'direct convention stands down)')
        }
      }
      window.__RIG_LIMITS__ = limits
        ? { state: 'trained', controls: limits.length, range: declaredRange, limits,
            ...(flags.rigClampPad != null ? { pad: flags.rigClampPad } : {}) }
        : {
            // `none` here means NOTHING clamps the ARKit path — either because the
            // flag asked for that, or (as before) because the bundle declares no
            // range to fall back to. Both are reported as the same fact.
            state: rigRangeRef.current ? 'declared-range' : 'none',
            range: rigRangeRef.current ?? null, limits: null,
            ...(wantClamp !== 'trained' ? { requested: wantClamp } : {}),
          }
      if (limits) {
        // How much of the declared range the training data actually covers — the
        // number that says whether a slider can reach an expression at all.
        const span = declaredRange[1] - declaredRange[0]
        const covered = limits.reduce((a, [lo, hi]) => a + (hi - lo), 0) / (limits.length * span)
        console.info(`[ogs-ht] rig limits: trained domain on ${limits.length} controls `
          + `(${(100 * covered).toFixed(0)}% of the declared [${declaredRange[0]}, ${declaredRange[1]}] range)`)
      }
      // TRAINED SUBSPACE over the WHOLE rig. The same "in-range on every axis, never
      // seen as a combination" failure applies to brows and eyes as to the mouth, and
      // a mouth-only fit never looks at them. Costs the non-mouth channels real
      // amplitude (measured on eyeline-v10c: blink keeps 63%, brow-outer-up 61%).
      // ?blinkmacro=0 — drive the blink control directly instead of the lid group.
      // Needed to reach a genuinely unconstrained rig: with every other switch off
      // this was still rewriting the lids, so "nothing is touching my rig" was not
      // actually expressible.
      blinkMacroRef.current = poseTable
        && new URLSearchParams(window.location.search).get('blinkmacro') !== '0'
        ? buildBlinkMacro({ names: packs.rigNames ?? [], presets: poseTable.presets, dim: poseTable.dim })
        : null
      if (blinkMacroRef.current) {
        console.info(`[ogs-ht] blink macro: ${blinkMacroRef.current.count} lid controls driven as a `
          + `configuration from trained row ${blinkMacroRef.current.closedRow} (gaze untouched)`)
      }
      // ONE switch, because scope and off are the same decision. ?rigpca=off|0
      // disables the projection, ?rigpca=region is the per-part per-side fit, and
      // absent is the single whole-rig default. The fit covers the WHOLE rig
      // either way — the same "in-range on every axis, never seen as a
      // combination" failure applies to brows and eyes, and a mouth-only fit never
      // looks at them (measured: blink keeps 63%, brow-outer-up 61%).
      //
      // GLOBAL by default: one fit over all 168 controls. It constrains hardest,
      // because the combinations that make the decoders over-respond do span
      // regions, and it is the tighter of the two sets — splitting the rig into
      // independently-projected blocks is a RELAXATION, so `region` is strictly
      // looser rather than differently-shaped. The cost is real and measured: the
      // global fit COUPLES regions the data says are independent — the two mouth
      // corners correlate at -0.075, yet it bleeds 0.081 of a left frown onto the
      // right, and a left wink moves 60 right-side controls (region: 0). That is
      // what ?rigpca=region exists to A/B.
      const pca = resolveRigProjection(window.location.search)
      const buildScope = (scope) => (!poseTable || !pca.enabled ? null
        : scope === 'global'
          ? buildMouthSubspace({
            presets: poseTable.presets, rows: poseRows, cols: poseTable.dim,
            mouthIdx: Array.from({ length: poseTable.dim }, (_, i) => i),
            extraPoses: arkitBase ? [arkitBase] : [],
          })
          : buildRegionalSubspaces({
            presets: poseTable.presets, rows: poseRows, cols: poseTable.dim,
            names: packs.rigNames ?? [], groupBy: regionKey,
            extraPoses: arkitBase ? [arkitBase] : [],
          }))
      // Each scope is built at most once — a fit is ~140 ms of Jacobi — and kept, so the
      // one the gate settles on is installed rather than rebuilt.
      const builtScopes = new Map()
      const fitFor = (scope) => {
        if (!builtScopes.has(scope)) builtScopes.set(scope, buildScope(scope))
        return builtScopes.get(scope)
      }
      mouthPcaRef.current = fitFor(pca.scope)
      // THE FIT HAS TO LEAVE THE CHARACTER ABLE TO SPEAK, and whether it does is a
      // property of the BUNDLE's table, not of the code: the basis spans no more
      // directions than the table has distinct poses. Measure it here, on this
      // bundle's own ARKit basis — the 52 single-channel expressions the live path
      // actually composes — and drop the projection when it keeps less than half of
      // the median one. Only when nobody asked for a scope: a typed ?rigpca= is a
      // decision, and this must not overrule it in either direction.
      const channelPoses = arkitChannelPoses(arkitBase)
      const measured = new Map()
      const retentionOf = (scope) => {
        if (!measured.has(scope)) {
          const fit = fitFor(scope)
          measured.set(scope, fit ? subspaceRetention(fit, arkitBase, channelPoses) : null)
        }
        return measured.get(scope)?.median ?? null
      }
      // Try the resolved scope; on a table too small for it, fall back to the LOOSER
      // regional fit before giving up on the constraint — no projection at all is what
      // leaves the idle ARKit animation blowing the face out (chooseProjectionScope).
      const chosen = chooseProjectionScope({ scope: pca.scope, explicit: pca.explicit, retentionOf })
      const effectiveScope = chosen.scope
      mouthPcaRef.current = chosen.dropped ? null : fitFor(effectiveScope)
      const retention = measured.get(effectiveScope) ?? null
      if (chosen.dropped) {
        console.error(`[ogs-ht] rig subspace DISABLED — this bundle's ${poseRows}-row pose table `
          + `spans too few directions to speak through: it keeps only ${(100 * chosen.tried[pca.scope]).toFixed(0)}% `
          + `of the median single-channel ARKit expression at scope '${pca.scope}'`
          + (chosen.tried.region != null && pca.scope !== 'region'
            ? `, and ${(100 * chosen.tried.region).toFixed(0)}% at the looser 'region' scope` : '')
          + ` (a rich table keeps 80-94%). The face would be frame-for-frame identical while talking, `
          + `and is now UNCONSTRAINED — expect it to over-respond. Force one back on with `
          + `?rigpca=global|region, and re-export the bundle with more trained poses for the real fix.`)
      } else if (chosen.fellBack) {
        console.warn(`[ogs-ht] rig subspace: '${pca.scope}' keeps only `
          + `${(100 * chosen.tried[pca.scope]).toFixed(0)}% of the median single-channel ARKit expression on `
          + `this ${poseRows}-row table, so the LOOSER 'region' fit is used instead `
          + `(${(100 * chosen.retention).toFixed(0)}%). An unconstrained rig over-responds; a relaxed `
          + `constraint still removes the combinations the capture never contained.`)
      } else if (retention) {
        console.info(`[ogs-ht] rig subspace keeps ${(100 * retention.median).toFixed(0)}% of the median `
          + `single-channel ARKit expression (worst ${(100 * retention.min).toFixed(0)}%)`)
      }
      const sub = mouthPcaRef.current
      window.__MOUTH_SUBSPACE__ = sub
        ? (sub.regions
          ? { state: 'applied', scope: effectiveScope, fellBack: chosen.fellBack, regions: sub.regions,
              dims: sub.regions.reduce((a, r) => a + r.dims, 0),
              components: sub.regions.reduce((a, r) => a + r.k, 0) }
          : { state: 'applied', scope: effectiveScope, fellBack: chosen.fellBack, components: sub.k, dims: sub.dims,
              poses: sub.poses, energy: +sub.energy.toFixed(4), coordsOf: sub.coordsOf })
        : {
            state: !pca.enabled ? 'disabled'
              : (retention ? 'too-tight' : 'none'),
            scope: effectiveScope,
            ...(retention ? { retention: +retention.median.toFixed(3) } : {}),
            ...(chosen.tried ? { tried: chosen.tried } : {}),
          }
      if (sub) {
        console.info(`[ogs-ht] rig subspace: scope=${effectiveScope}`
          + (sub.regions
            ? ` — ${sub.regions.map((r) => `${r.region}(${r.dims}c/${r.k}k)`).join(' ')}`
            : ` — one fit, ${sub.k} components over ${sub.dims} controls`)
          + '; ?rigpca=off|region')
      }
      console.info(`[ogs-ht] deform anchor: ${restRig ? 'pose-table row 0' : 'ZERO rig (no pose table)'}`)
      // Trained mouth references for the live A2F path (liveRigRegions). Null
      // when the bundle declares neither a mouth_controller nor macros — the
      // live path then keeps plain rest+delta, region mask still applies.
      //
      // PICKED AGAINST `arkitBase`, NOT against row 0. buildLiveMouthDrive uses
      // the pose it is handed only to choose which closed/open rows to
      // interpolate between, and the live rig is composed onto arkitBase — so
      // handing it row 0 chooses references for a face the live path never
      // shows. Measured on eyeline-v10c, that mismatch is not academic: with
      // row 0 the closed reference is row 201 while the base is row 85, so at a
      // shut mouth the mix pulls 65% toward ANOTHER row's lips over this row's
      // face — a two-row chimera, and idle goes from 0.0000 off the trained
      // manifold to 0.1979. Handing it the base makes the closed reference the
      // base row itself, so t is exactly 0 at rest and the mix is a true no-op
      // there. It is also strictly better everywhere else (JawOpen=1 0.513 ->
      // 0.456, COMBO-5 0.572 -> 0.526) — the references now share a face with
      // the thing they are blended into.
      liveMouthDriveRef.current = poseTable
        ? buildLiveMouthDrive({
            scene: packs.scene, names: packs.rigNames,
            presets: poseTable.presets, dim: poseTable.dim, rest: arkitBase,
          })
        : null
      // A bundle with neither a mouth_controller nor macros gets no drive, and the
      // weight is then inert whatever it says — so the row offers no slider rather
      // than a dead one.
      armLiveMouthWeight(urlMouthWeightRef.current, { available: !!liveMouthDriveRef.current })
      if (liveMouthDriveRef.current) {
        const d = liveMouthDriveRef.current
        console.info(`[ogs] live mouth drive ready (${d.source}): refs ${d.closedRow}->${d.openRow}, `
          + `${d.subset.length} controls, manifold weight ${liveFaceFlagsRef.current.mouthWeight}`)
      }
      // A REFUSED fit is the one rig failure the loader cannot see: the pack was
      // present and parsed, so rigMode resolved 'orl' happily, and only the
      // calibration knows it belongs to a different character. Surface it under
      // the same pill, with the cause that names the actual fix.
      const refusedRigs = await engine.calibrate(restRig)
      // Idle at the SAME vector the live path composes onto, so the initial decode
      // and the first ARKit frame agree instead of popping.
      engine.setIdleRig(arkitBase)
      if (cancelled) return
      if (refusedRigs?.length) {
        reportRigProblem(resolveCharacterId(), {
          cause: 'mismatch',
          detail: refusedRigs.map((r) => `${r.name}: ${r.error}`).join('; '),
        })
      }

      // Per-pose anchoring, AFTER calibrate: each branch's row-0 fit is what the
      // baked deltas compose onto, so there is nothing to install before it.
      installPoseAnchors(engine, packs, poseTable)


      // Live fp16<->fp32 swap from the Avatar node's dropdown — serialized
      // through the engine's own mutex, then the current rig is replayed.
      appliedPrecisionRef.current = precision
      applyPrecisionRef.current = async (p) => {
        await engine.setPrecision(precisionPrefersFp16(p))
        reportHtPrecision(p)
      }

      // With the mesh view replacing the splats, the renderer keeps running (the
      // mesh is posed by the same decode) but nothing presents them. `both` is the
      // overlay, so the two can be compared in one frame.
      const splatsVisible = flags.meshView === 'off' || flags.meshView === 'both'
      isaacSplat.attachEngine({
        rigDim: engine.rigDim,
        runFull: (rig) => engine.runFull(rig),
        runAppearance: () => engine.runAppearance(),
        setOpacityMask: () => {},
        setSplatVisible: (v) => updater.setVisible(v && splatsVisible),
      })
      updater.setVisible(gateActiveRef.current && splatsVisible)

      // Blueprint drive: each plugin.ogs.branch node switches ITS region on/off.
      // A region no node in the graph names renders — so a blueprint that only
      // wires avatar→renderer still shows the full face, and switching the head
      // off never takes the teeth with it.
      applyRegionsRef.current = (nodes, edges) => {
        const states = branchRegionStates(nodes, edges)
        engine.setRegions(Object.fromEntries(branches.map((br) => [br.name, states[br.name] ?? true])))
      }
      applyRegionsRef.current(faceGraphState.nodes, faceGraphState.edges)

      // Dev/test handle: poke a single control in the shared rig space (e.g.
      // jaw open) and watch head + teeth follow, bypassing the face graph.
      // Live lip-sync itself runs through driveSplatFace (ARKit → the gather
      // derived from this bundle's rig_names.json → setRigDirect).
      const liveRig = new Float32Array(engine.baseRig())
      window.__OGS_HT_SET_CONTROL__ = (idx, val) => {
        if (idx < 0 || idx >= liveRig.length) return null
        liveRig[idx] = val
        engine.runFull(new Float32Array(liveRig))
        return { idx, val, rigDim: liveRig.length }
      }
      window.__OGS_HT_ENGINE__ = engine

      // Initial kick: decode + present the REST pose (engine.baseRig() is the
      // rest rig calibrate() was anchored at, or zeros for a bundle with no pose
      // table). It has to be the same rig as the anchor: decoding the opening
      // frames at zeros while anchoring at row 0 is what made eyeline V10-C boot
      // off-neutral and then snap into place on the first ARKit update.
      await engine.runFull(engine.baseRig())
      if (cancelled) return
      console.info(`[ogs-ht] ready — live head+teeth pipeline (${branches.length} branches, worldScale=${WORLD_SCALE})`)
      if (!cancelled) onReady?.()

      // The BODY rig, on this pipeline too. A mixed bundle (myra_v10: a live
      // 168-control head over body/clothes/hair in their own space) arrives here
      // rather than in the neutral pipeline below, and the part drive used to be
      // registered ONLY there — so such a character had a talking face and no way
      // to play a clip, a gesture or a generated walk at all. The rig is the same
      // one, registered the same lazy way; what differs is that the head is not
      // skinned from it here — it keeps the face deform and rides the head bone
      // (applyPartRigPose).
      registerPartDrive({
        packs, scn, branches, worldRotation: WORLD_ROTATION, worldScale: WORLD_SCALE,
        isCancelled: () => cancelled,
        isLive: (br) => engine.isLive(br),
        present: (pose) => engine.runPosed(pose),
        onPresent: (fn) => presentedHooks.push(fn),
        // The lift device, so the skinning lands in the buffer the jacobians read.
        device: lift.device,
      })

      // Orbit recolor is driven per frame from useFrame (camWatchRef) — see the
      // camera watch there. Nothing to wire here.
    }

    // Multi-region OGS pipeline (myra-ogs) on the NODE path: one live OGSBranch
    // per region (trunk→geom→appr + its own plücker/lifter — the same container
    // head+teeth uses), terminated by ONE OGSRenderer that lifts every branch and
    // presents the concatenation. Phase 2: static NEUTRAL pose (branch neutral
    // verts + zero rig); camera orbit re-colors. Phase 3 swaps neutral
    // verts for live GLB-skinned verts.
    const startMultiRegionPipeline = async (packs) => {
      const qp = new URLSearchParams(window.location.search)
      const flags = ogsGpuFlags()
      const providers = await resolveProviders(flags.ep, { preferWebgpu: true })
      if (cancelled) return

      const scn = await loadMultiRegionScene(packs.texBase, packs.scene)
      if (cancelled) return
      // Live-rig bundle: its own pipeline — per-branch deform, blueprint-gated
      // regions, and a re-decode on every rig push. Two ways in, and the second
      // is what lets a MIXED bundle animate:
      //   - `body_rig: 'head'` — the export that DECLARES every branch shares the
      //     head's control space (isaac V2 head+teeth, eyeline).
      //   - a bundle that declares nothing but whose HEAD is drivable anyway
      //     (myra_v10: head 168 + rig2mesh_head, body/clothes/hair in their own
      //     2397-wide space with no rig→verts step). The head follows the face
      //     graph; the others hold their own reference pose (HeadTeethEngine's
      //     rigFor/isLive). Read from the manifest's SHAPE, because nothing in
      //     such a bundle declares it — and reading it as "not a live-rig bundle"
      //     is what left this character's face frozen at neutral while its rig
      //     assets were sitting in the bundle unused.
      if (liveRigBranches(scn.manifest, packs.rigPlan?.rig ?? 'none').length) {
        await startHeadTeethPipeline(packs, scn, providers); return
      }
      // Eye Follow (ENG-1060): this pipeline decodes a static neutral after boot
      // (attachEngine's runFull ignores the rig once neutralDone), so a rig-level
      // eye aim has nothing to ride. Say so on the node rather than sitting mute.
      setSplatEyeFollowCapability({
        route: 'none',
        reason: 'Not supported on this splat bundle — its pipeline renders a static neutral pose (no live rig drive for the eye aim to ride).',
      })
      const WORLD_SCALE = scn.manifest.worldScale // myra bundles are metres (default 1)
      const ORIGIN_SCALE = 1 / WORLD_SCALE
      // Placement offset, subtracted from the camera before ORIGIN_SCALE so the
      // plücker moment is computed in the splat's local frame (plucker.ts).
      const WORLD_OFFSET = scn.manifest.worldOffset
      // myra_v5 trains 180° about Z, so the lift is right and the avatar hangs
      // upside down. Undone on the SplatMesh, inverted onto the plücker camera —
      // the decoders keep seeing the trained frame (render/worldRotation.ts).
      // Before anything reads the eye-region flags: the bundle gets to decline a
      // drive its capture cannot take, and both pipelines ask the same way.
      applyBundleFaceDrives(scn.manifest)
      const WORLD_ROTATION_DEG = resolveWorldRotation(scn.manifest.worldRotation)
      const WORLD_ROTATION = worldRotationQuaternion(WORLD_ROTATION_DEG)
      console.info(`[myra-ogs] multi-region pipeline: providers=${providers.join(',')} worldScale=${WORLD_SCALE} worldRotation=[${WORLD_ROTATION_DEG.join(', ')}]`)
      // Render path: WGSL GpuLifter (?gpulift=0 → CPU GaussianLifter) + the
      // terminal OGSRenderer, same as head+teeth.
      // Decoder precision picked on the OGS Avatar node. The shipped *_fp16.onnx
      // siblings were dead weight until the node path could select them; they
      // halve the (one-time here) decode. The ?fp16 / ?geomfp16 /
      // ?apprfp16 kill-switches still win inside the branch.
      const precision = readAvatarPrecision(faceGraphState.nodes)
      const preferFp16 = precisionPrefersFp16(precision)

      // ORDER IS LOAD-BEARING (gpuDevice.ts): prepare() installs the shareable
      // adapter and MUST run before the first session create; resolve() reads the
      // device ORT actually built and MUST run after.
      const prepared = await prepareOgsLiftDevice(ogsOrt, providers, flags)
      if (cancelled) return

      // The myra bundles land here — chosen by SUBJECT, so a non-myra bundle is untouched.
      const liftChoice = chooseLift({ subject: scn.manifest.subject, character: resolveCharacterId() }, flags.myraLift)
      console.info(`[myra-ogs] ${liftChoice.impl} lift (${liftChoice.reason})`)

      const branches = scn.branches.map((b, i) => new OGSBranch({
        id: `branch_${b.name}`,
        name: b.name,
        members: [`branch_${b.name}.geom`, `branch_${b.name}.appr`],
        // The renderer's input-port pair for this region — the same names
        // plugins/ogs-renderer/plugin.json declares (graph/branchPorts.ts).
        ports: branchPortNames(b.name, i),
        live: {
          assets: b,
          providers,
          getBytes: packs.getBytes,
          fetchBytes: packs.fetchBytes,
          preferFp16,
          originScale: ORIGIN_SCALE,
          worldOffset: WORLD_OFFSET,
          worldRotation: WORLD_ROTATION,
          onDowngrade: reportDowngrade,
          flags,
          lift: liftChoice.impl,
          // Only the exact rig reads the control names — they are mapped onto
          // the DNA's GUI controls, and the rig itself comes out of the mounted
          // orl_* character assets. The plan says WHICH rig the loader fetched.
          rigNames: packs.rigNames,
          rigPlan: packs.rigPlan,
        },
      }))
      // Publish BEFORE any session is built — see startHeadTeethPipeline: a
      // cancel between here and the end of init must still release the sessions.
      liveBranches = branches
      // Debug seam: the live OGSBranch instances, so a console/test can read a
      // branch's lifter output (positions/scales/opacities/colors) and compare it
      // against a reference implementation. Read-only; never on the render path.
      window.__OGS_BRANCHES__ = branches
      const ctxFor = (br, sharedDevice) => ({
        ort: ogsOrt, uvRes: br.uvRes, rigDim: br.rigDim, sharedDevice, gpuBufferIo: prepared.shared,
      })
      // Phase 1 — sessions only (ORT's device doesn't exist yet).
      for (const br of branches) {
        await br.init(ctxFor(br, null))
        if (cancelled) return
      }
      const lift = await resolveOgsLiftDevice(ogsOrt, prepared)
      if (cancelled) return
      // ORT built a device the lifter can't share: the gpu-buffer hints are baked
      // in at create time, so the only repair is to download every output.
      if (lift?.demoted) for (const br of branches) br.setDownloadOutputs(true)
      const sharedDevice = lift?.mode === 'shared' ? lift.device : null
      // Phase 2 — plücker + lifter on the resolved device. SoA (packed:false):
      // the phase-3 GLB drive culls over-stretched splats through the SoA
      // fields, which a packed lift leaves stale.
      for (const br of branches) {
        await br.initGpu(ctxFor(br, sharedDevice), lift.device, { packed: false })
        if (cancelled) return
      }
      for (const br of branches) {
        console.info(`[myra-ogs] branch '${br.name}' ready: V=${br.neutralVertices.length / 3} uv=${br.uvRes} rigDim=${br.rigDim} lift=${br.liftKind()} plucker=${br.pluckerKind()} geom=${br.geomUsedFp16 ? 'fp16' : 'fp32'} appr=${br.apprUsedFp16 ? 'fp16' : 'fp32'}`)
      }
      reportLift('myra-ogs', liftChoice, branches)
      // Every branch resolves precision the same way — publish the first so the
      // Avatar node shows what actually built, after any auto-downgrade.
      reportPrecision({
        requested: precision,
        geom: branches[0]?.geomUsedFp16 ? 'fp16' : 'fp32',
        appr: branches[0]?.apprUsedFp16 ? 'fp16' : 'fp32',
      })

      ensureSpark()
      updater = new SplatMeshUpdater(avatarRoot)
      updater.meshScale = WORLD_SCALE
      // Must match the offset the plücker origin is computed against (passed to
      // the branches as `worldOffset` above): conditioning appr on a camera in a
      // local frame the mesh is not placed in is the same error, mirrored. Same
      // for the rotation, which the branches got INVERTED for exactly that reason.
      updater.meshOffset = WORLD_OFFSET
      updater.meshRotation = WORLD_ROTATION

      const avatar = window.__AVATAR_SCENE__
      if (avatar) { avatarPrevVisible = avatar.visible; avatar.visible = false }

      // ONE slot for the body drive and the recolour; a recolour asked mid-frame is kept, not dropped.
      const slot = createFrameSlot()
      let neutralDone = false
      let driving = false

      // Frame the camera BEFORE the appearance decoders run: appr's plücker is
      // view-dependent, and a degenerate boot camera silently corrupts the colors
      // of every surface facing away from it (crystalline neck/face junk). Frame
      // from the neutral verts — no splats exist yet (same as head+teeth).
      // Also the Frame Avatar / F command's implementation for this character:
      // the splat extent lives in these branches, not in a rig scene graph.
      const frameToBranches = () => {
        const V = branches.reduce((n, b) => n + b.neutralVertices.length, 0)
        if (!V) return false
        const pos = new Float32Array(V)
        let off = 0
        for (const b of branches) { pos.set(b.neutralVertices, off); off += b.neutralVertices.length }
        return frameCameraToSplats(camera, { out: { positions: pos, opacities: new Float32Array(V / 3), count: V / 3 } }, WORLD_SCALE, WORLD_OFFSET, { preserveDirection: booted, worldRotation: WORLD_ROTATION })
      }
      frameToBranches()
      booted = true
      unregisterFramer = setContentFramer(frameToBranches)

      // --- lift + present plumbing ---------------------------------------------
      let ogsRenderer = null
      const branchCull = new Array(branches.length).fill(0)
      // Per-branch lift wrapper. Two things OGSRenderer has no source option for:
      //  (a) skipUvUpload — the phase-3 drive re-lifts the SAME frozen decoder
      //      outputs at new verts, so re-uploading the UVs every frame is waste;
      //  (b) the foreign-clip over-stretch cull, which has to run on the SoA
      //      fields before the concat.
      const drivenLifter = (br, i) => {
        let lastGeom = null, lastColor = null
        return {
          lift: async (verts, geomUv, colorUv) => {
            const skipUvUpload = lastGeom === geomUv && lastColor === colorUv
            lastGeom = geomUv; lastColor = colorUv
            const g = await br.lifter.lift(verts, geomUv, colorUv, { skipUvUpload })
            branchCull[i] = driving ? sanitizeDrivenGaussians(g) : 0
            return g
          },
        }
      }
      // ONE terminal pass: the renderer lifts every source it got UVs for and
      // presents the concatenated set, so a frame costs ONE present (readbacks
      // are still per branch — see OGSRenderer's header).
      const presentFrame = async (uvs) => {
        const inbound = {}
        branches.forEach((br, i) => {
          if (!uvs[i]) return
          const [geomPort, colorPort] = br.outputs.map((p) => p.name)
          inbound[geomPort] = uvs[i].geom_uv
          inbound[colorPort] = uvs[i].color_uv
        })
        await ogsRenderer.evaluate({ rig: null, camera: localCam.update(camera, avatarRoot), rigEpoch: 0, cameraEpoch: 0 }, inbound)
        const count = ogsRenderer.lastCount
        window.__ISAAC_SPLAT_COUNT__ = () => count
        return count
      }

      // Which regions render, by branch name — blueprint-driven (applyRegions
      // below). Every region starts on, so a graph naming none renders all.
      const regionOn = new Map(branches.map((br) => [br.name, true]))

      const composite = async (mode) => {
        if (cancelled || !gateActiveRef.current) return
        if (mode === 'appearance' && !neutralDone) return
        if (!slot.acquire()) return
        try {
          // Consumed BEFORE decoding: a view that moves mid-decode is asked for again.
          slot.consumeAppearance(performance.now())
          const uvs = []
          for (const br of branches) {
            // A region switched off decodes nothing; the renderer skips its
            // source too, so its splats leave the frame.
            if (!regionOn.get(br.name)) { uvs.push(null); continue }
            // A region switched back ON has no cached geom to recolor — the first
            // pass after it returns is a full decode, whatever the mode asked for.
            // appr's plücker is built from the camera in the AVATAR's frame — the trained one.
            const view = localCam.update(camera, avatarRoot)
            uvs.push(mode === 'full' || !br.lastGeomUv
              ? await br.renderFull(br.neutralRig(), view)
              : await br.renderAppearance(view))
            if (cancelled) return
          }
          const count = await presentFrame(uvs)
          window.__MYRA_OGS__ = {
            branches: branches.map((b) => b.name), regions: Object.fromEntries(regionOn),
            count, worldScale: WORLD_SCALE,
          }
          if (mode === 'full') neutralDone = true
        } finally { slot.release() }
      }

      // No per-branch hidden-texel cull: every branch renders every texel it decodes.
      // ONE terminal render node for every branch: it lifts each source that
      // produced UVs this pass and presents the concatenation once. Sources are
      // SoA here (packed:false above), so present() takes the concatGaussians
      // path and SplatMeshUpdater rebuilds the PLY — unchanged from before.
      ogsRenderer = ogsRendererPlugin.create({
        id: 'renderer',
        updater,
        sources: branches.map((br, i) => ({
          name: br.name,
          ports: br.outputs.map((p) => p.name),
          lifter: drivenLifter(br, i),
          getVerts: () => br.verts,
          capacity: br.uvRes * br.uvRes,
        })),
      })
      await ogsRenderer.init(ctxFor(branches[0], sharedDevice))
      if (cancelled) { updater.dispose(); return }
      // Engine: the face-graph rig drive (setRigDirect→runFull) is a no-op once the
      // neutral pose is up (Phase 2 is static); camera orbit re-colors. Phase 3
      // will feed live verts through runFull.
      isaacSplat.attachEngine({
        runFull: () => { if (!neutralDone) composite('full') },
        // While the drive paces frames the recolour rides the next posed frame instead.
        runAppearance: () => { if (slot.requestAppearance(performance.now())) composite('appearance') },
        setOpacityMask: () => {},
        setSplatVisible: (v) => updater.setVisible(v),
      })
      updater.setVisible(gateActiveRef.current)

      // Blueprint drive: each plugin.ogs.branch node switches ITS region on/off,
      // and the renderer holds the same switch — a region that is off is never
      // decoded and never lifted, so one branch can be isolated live to find
      // which part of a bundle is wrong. A region with no node in the graph
      // renders (branchRegionStates leaves it absent), so a blueprint that only
      // wires avatar→renderer still shows the whole avatar. The eyes ride the
      // same call: their mount is read through the outer binding, so gating them
      // works whether or not it exists yet.
      //
      // A toggle that lands mid-decode must still reach the screen: composite()
      // drops re-entrant calls, and nothing else re-presents a static avatar, so
      // without the retry the toggle would read as dead until the next camera move.
      const presentRegions = () => {
        if (cancelled) return
        if (slot.inFlight) { setTimeout(presentRegions, 50); return }
        composite(neutralDone ? 'appearance' : 'full')
      }
      const applyRegions = (nodes, edges, { present = true } = {}) => {
        const states = branchRegionStates(nodes, edges)
        let changed = false
        for (const br of branches) {
          const on = states[br.name] ?? true
          if (regionOn.get(br.name) === on) continue
          regionOn.set(br.name, on)
          ogsRenderer.setSourceEnabled(br.name, on)
          console.info(`[myra-ogs] branch '${br.name}' ${on ? 'on' : 'off'} (blueprint)`)
          changed = true
        }
        if (eyesMount) {
          const eyes = branchNode(nodes, 'eyes')
          if (eyes) eyesMount.setLookAtCamera(eyes.data?.lookAtCamera ?? true)
          eyesMount.setEnabled(states.eyes ?? true)
        }
        if (changed && present) presentRegions()
      }
      applyRegionsRef.current = (nodes, edges) => applyRegions(nodes, edges)
      // `present:false` — the initial composite below is the one that draws.
      applyRegions(faceGraphState.nodes, faceGraphState.edges, { present: false })

      await composite('full')
      console.info(`[myra-ogs] ready — neutral gaussian render (${branches.length} branches)`)
      if (!cancelled) onReady?.()

      if (scn.manifest.branches.eyes?.gazeConditioned && qp.get('eyes') !== '0') {
        try {
          eyesMount = await mountEyeBranch({
            scene, camera, spec: scn.manifest.branches.eyes,
            getBytes: packs.getBytes,
            isActive: () => !cancelled && gateActiveRef.current,
          })
          if (cancelled) { eyesMount.dispose(); eyesMount = null; return }
          eyesMountRef.current = eyesMount
          eyesMount.setVisible(gateActiveRef.current)

          // The eyes join the same blueprint drive — applyRegions reads the mount
          // through this binding, so it gates them (and their eye-contact policy)
          // from here on. No branch node for them → they stay on.
          applyRegions(faceGraphState.nodes, faceGraphState.edges, { present: false })
        } catch (error) {
          console.error('[myra-ogs] eye branch not mounted:', error?.message || error)
          reportDowngrade?.(`eyes: ${error?.message || error}`)
        }
      }

      // Orbit recolor is driven per frame from useFrame (camWatchRef), same as every
      // other pipeline here — appr's colors are view-dependent, so camera moves must re-run
      // it at the live view, and the engines above guard/coalesce re-entrancy themselves.

      // Phase 3: drive the gaussians from LIVE SKINNED verts (frozen appearance).
      // Posed by the body blueprint and by a clip preview — see MultipartOGSModel.
      //
      // This is NOT a second pipeline. The lift, the per-branch lifters and the
      // terminal present are the SAME node pipeline everything else uses
      // (br.lifter / br.uvOutputs() / presentFrame → ogsRenderer.evaluate). The
      // only thing that differs is the VERTEX SOURCE: head+teeth bundles get
      // verts from the deform (control vector → ONNX → mesh), while these get them
      // from a SKELETON — rig.glb's per-part skinned meshes posed by the body blueprint
      // and scattered into branch-vertex order through the branch mesh's own
      // `faces` (partDriver.js). Decoder outputs stay FROZEN across the drive
      // (body motion changes geometry, not appearance), so a frame costs
      // jacobians + lift and no ORT at all.
      //
      // PER PART, never a fused rig, and no vertex association: a union skeleton
      // gives the clip duplicate bone names to bind against and picks one
      // arbitrarily, while `faces[corner]` already names the trained vertex each
      // GLB corner belongs to — so the correspondence is exact and free, and each
      // part solves its OWN FBX→branch frame (the head's offset is 55 mm, the
      // body's 22 mm; one shared conversion renders the body and breaks the head).
      //
      // Not quite frozen: a root turn is a view change; appr re-runs once it settles (splatFrameSlot.js).
      //
      // Reached by DATA, not by a URL param: it needs a bundle shipping the rig, and
      // nothing else. The build is LAZY — registered with partDriveRegistry so whoever
      // poses the body pays for it on demand.
      const rigUrl = partRigUrl((name) => !!packs.getBytes(name), packs.texBase)
      if (!rigUrl) console.info(`[myra-ogs] no ${PART_DRIVER_RIG} in this bundle — no body rig to pose`)
      if (rigUrl) {
        const buildDriver = async () => {
          const driver = await loadPartDriver({
            rigUrl,
            branchMeshes: Object.fromEntries(scn.branches.map((b) => [b.name, b.mesh])),
          })
          if (cancelled) { driver.rig.dispose(); return }
          // The rotation the rig's own space is RENDERED through: the solved part fit
          // (rig frame -> trained frame) then the bundle's world_rotation (trained ->
          // world). A kimodo clip states WORLD orientations, so anything building one
          // against this rig needs the inverse (lib/bodyMotionClip.js). Composed here
          // because this is the one scope that knows both halves.
          driver.rigToWorld = rigToWorldRotation(driver.verts, WORLD_ROTATION)
          // And its SCALE: rig.glb is centimetres (the part fit's cm→m), while a
          // generated clip's root translation is world metres.
          driver.rigToWorldScale = rigToWorldScale(WORLD_SCALE)
          window.__OGS_DRIVER__ = driver
          window.__MYRA_OGS_BRANCHES__ = branches
          // The frame contract, per branch and per part: a correct conversion lands
          // within the bind-vs-neutral pose difference. Logged because
          // maxDisagreement reads ~0 even when every vertex is in the wrong frame.
          for (const branch of driver.fit) console.info(`[myra-ogs] ${formatPartDriverFit(branch)}`)

          // Null until something binds a clip — the rig arrives with a skeleton only.
          const duration = driver.rig.clip?.duration ?? null
          // Own buffer per branch — neutralVertices must never be mutated, and the
          // no-per-frame-alloc rule means it cannot be made inside driveAt.
          const rigidVerts = new Map(branches
            .filter((br) => !driver.verts.branches[br.name])
            .map((br) => [br.name, new Float32Array(br.neutralVertices.length)]))
          // The root is placed at the time a pose LANDED, never at the caller's clock, or the
          // character skates while its legs step. This pipeline awaits its own present, so
          // the end of poseAndPresent IS the drawn pose.
          let posedTime = null
          /** Re-run appr for every live region at the current view — the frozen colour is stale. */
          const recolour = async () => {
            for (const br of branches) {
              if (!regionOn.get(br.name) || !br.lastGeomUv) continue
              await br.renderAppearance(localCam.update(camera, avatarRoot))
              if (cancelled) return
            }
          }
          const poseAndPresent = async (t) => {
            driving = true
            try {
              const t0 = performance.now()
              driver.rig.setTime(t)
              // Each branch's pose verts ARE its OGSBranch.verts (what the
              // renderer source reads); jacobians follow the new positions.
              //
              // A branch with no part in the rig GLB — the HAIR — rides the head bone's
              // rigid delta instead. The groom export carries the trained poses and
              // nothing else, so there is no hair geometry for this clip; training
              // solves the same thing as a rigid map (build_macarena_hair.py). It
              // reproduces head motion and NOTHING else: a gathered bun is well
              // described by it, swing and lag are not.
              // Same three routes the live pipeline takes (partBodyPose.js), with no
              // live branch here: this pipeline's head is skinned from the rig like
              // every other part.
              applyPartRigPose({ branches, verts: driver.verts, headDelta: driver.rig.headDelta(driver.verts.fitForPart?.(driver.rig.headRestPart)), rigidVerts })
              const deformMs = performance.now() - t0
              // The view settled (or the colour is overdue): appr re-runs in THIS frame, with this pose.
              if (slot.consumeSettledAppearance(performance.now())) await recolour()
              if (cancelled) return
              const apprMs = performance.now() - t0 - deformMs
              // Re-lift at the new verts with the decoder outputs held (drivenLifter skips the UV re-upload).
              const count = await presentFrame(branches.map((br) => br.uvOutputs()))
              const now = performance.now()
              window.__MYRA_OGS__ = {
                ...window.__MYRA_OGS__, count, culled: branchCull.reduce((a, b) => a + b, 0), drivenTime: +t.toFixed(3),
                perf: {
                  deformMs: +deformMs.toFixed(1), apprMs: +apprMs.toFixed(1),
                  liftMs: +(now - t0 - deformMs - apprMs).toFixed(1), frameMs: +(now - t0).toFixed(1),
                },
              }
              posedTime = t
            } catch (e) {
              console.error('[myra-ogs] driven frame failed:', e)
            } finally { slot.release() }
          }
          /** Pose at `t` and present; true when the frame is TAKEN. `null`: nobody poses — run a pending recolour. */
          const driveAt = (t) => {
            slot.setDriveOwned(t != null)
            if (t == null) {
              if (slot.appearanceStale) composite('appearance')
              return false
            }
            if (cancelled || !gateActiveRef.current || !slot.acquire()) return false
            void poseAndPresent(t)
            return true
          }
          window.__MYRA_OGS_DRIVE__ = { driveAt, duration }
          // Name the rigid riders: a branch that quietly holds neutral for the whole
          // dance reads as a broken drive, and the cause is one missing part GLB.
          const riders = [...rigidVerts.keys()]
          const rigid = !riders.length ? 'none'
            : driver.rig.headDelta() ? `${riders.join(', ')} on the head bone`
              : `${riders.join(', ')} STATIC — the rig GLB has no head bone`
          console.info(`[myra-ogs] phase3 part drive ready (${branches[0]?.liftKind()} lift, `
            + `${driver.rig.parts.length} parts, ${duration ? `${duration.toFixed(2)}s clip` : 'no clip yet'}`
            + `, rigid: ${rigid})`)
          return { driveAt, duration, rig: driver.rig, rigToWorld: driver.rigToWorld, rigToWorldScale: driver.rigToWorldScale, posedTime: () => posedTime }
        }

        // The rig is registered, not a clip: whoever poses the body builds it. Never after
        // teardown: reset() has run by then, and a late registration would hand the next
        // character a builder over this disposed scene.
        if (cancelled) return
        partDriveRegistry.register({ build: buildDriver })
      }
    }

    // Phase 'rendering' (first entry) — the full inference pipeline. Runs once.
    const startPipeline = async () => {
      if (initStarted || cancelled) return
      initStarted = true
      // No MODEL / RIG_DIM / WORLD_SCALE here: each pipeline starter derives its
      // own from the bundle's scene manifest (scn.manifest.worldScale).
      // OGSModel only mounts for /ogs-bundle characters (isSplatCharacter ⇐ the
      // bundle probe), so the bundle is the sole asset source — loud failure on a
      // missing file, never a local fallback (progress on window.__OGS_CONTENT_PACKS__).
      const packs = await loadOgsContentPacks(resolveCharacterId())
      if (cancelled) return
      // Mesh-only bundle: the mount probe already owns rendering via MeshClipPreview.
      if (packs.meshOnly) {
        setSplatEyeFollowCapability({
          route: 'none',
          reason: 'Not supported on this bundle — it is a mesh-only preview (rig.glb + clips), with no live rig drive for the eye aim to ride.',
        })
        return
      }
      // Lip-sync wiring: the gather comes from the bundle's rig_names.json, so
      // driveSplatFace pushes rigs in the control space THIS bundle's decoders
      // were trained on (60 for isaac V1, 174 for the V2 head+teeth export).
      setLipSyncGather(packs.rigNames)
      // Raw rig board (ENG-946): the same rig_names.json, exposed as a control
      // space the Rig Preview panel can address DIRECTLY — every control the
      // decoders know, not just the subset ARKit-52 can reach. The pose table
      // (scene.rig_presets) loads lazily, only if someone opens that board.
      registerRigBoard({
        names: packs.rigNames,
        scene: packs.scene,
        fetchBytes: packs.fetchBytes,
        setRig: (rig) => {
          isaacSplat.setRigDirect(rig)
          // Keep the ARKit path's change-gate in sync so handing control back
          // doesn't get swallowed as "no change since last push".
          if (lastRigRef.current?.length === rig.length) lastRigRef.current.set(rig)
          haveSentRef.current = true
        },
      })
      // EVERY bundle arrives multi_region. A single-region bundle (schema_version
      // 1: flat geom/appr/mesh, no scene.json) is adapted into a one-branch
      // multi_region scene by ogsContentPacks (assets/sceneManifest.js's
      // singleRegionScene), so there is one pipeline rather than a second one
      // that exists only for that layout. Head+teeth (body_rig === 'head')
      // splits off inside.
      await startMultiRegionPipeline(packs)
    }

    // Drive init/render off the renderer-chain phase (not splatMode). The
    // blueprint's OGS renderer node is load-bearing: connecting it starts the
    // render, breaking/deleting it drops back to three.js. First 'rendering'
    // lazily boots the pipeline; later toggles just show/hide + recolor.
    const applyPhase = (next) => {
      // Render-block detection (ENG-991): feed every phase transition until the
      // pipeline has started. After initStarted, a drop to 'three'/'created' is
      // the author deliberately disconnecting the chain in the editor — a
      // legitimate editing state, never a stuck boot. Leaving a reported block
      // ('rendering' arrives) clears the App diagnostic live, no reload.
      if (!initStarted) {
        const change = blockDetector.notePhase(next, Date.now())
        if (change === 'unblocked' && !cancelled) onRenderBlocked?.(null)
      }
      if (next === 'three') {
        gateActiveRef.current = false
        if (updater) updater.setVisible(false)
        eyesMount?.setVisible(false)
        console.info('[isaac] no OGS renderer node — using three.js (base WebGLRenderer only)')
        return
      }
      if (next === 'created') {
        gateActiveRef.current = false
        ensureSpark()
        if (updater) updater.setVisible(false)
        eyesMount?.setVisible(false)
        return
      }
      // 'rendering'
      ensureSpark()
      gateActiveRef.current = true
      if (!initStarted) {
        console.info('[isaac] renderer chain connected — starting render')
        // A console.error alone leaves the user on App's loading overlay forever:
        // onReady never fires, so nothing clears it and nothing says why. Surface
        // it the way StreamAvatarModel does — the App-level error overlay — so an
        // unsupported bundle, a missing /ogs file or a decoder build failure is
        // actually visible instead of only loud in devtools.
        startPipeline().catch((e) => {
          console.error('[isaac] init failed', e)
          if (!cancelled) onError?.(e)
        })
      } else {
        console.info('[isaac] renderer chain reconnected — resuming render')
        if (updater) updater.setVisible(true)
        eyesMount?.setVisible(true)
        isaacSplat.runAppearance()
      }
    }
    applyPhaseRef.current = applyPhase
    // Apply whatever phase the blueprint already resolves to (the shipped isaac
    // blueprint has the chain wired → 'rendering'). Seed the phase memo so
    // checkPhase only fires applyPhase on subsequent editor edits.
    const initialPhase = resolveRenderPhase(faceGraphState.nodes, faceGraphState.edges)
    phaseRef.current = initialPhase
    phaseNodesRef.current = faceGraphState.nodes
    phaseEdgesRef.current = faceGraphState.edges
    applyPhase(initialPhase)

    return () => {
      cancelled = true
      clearInterval(blockPoll)
      // A character switch unmounts this pipeline — any reported block belongs
      // to the OLD character and must not survive onto the next one's boot.
      onRenderBlocked?.(null)
      // Same rule for the part drive: a registration left standing would hand the next
      // character a builder over this disposed scene.
      partDriveRegistry.reset()
      for (const skinner of livePartSkinners?.values() ?? []) skinner.dispose()
      livePartSkinners = null
      for (const skinner of liveBodySkinners.values()) skinner.dispose()
      liveBodySkinners.clear()
      unregisterFramer?.()
      unregisterFramer = null
      applyPhaseRef.current = null
      applyPrecisionRef.current = null
      applyRegionsRef.current = null
      if (meshPreviewRef.current) {
        meshPreviewRef.current.dispose()
        meshPreviewRef.current = null
        delete window.__OGS_MESH_PREVIEW__
      }
      // No camera 'change' listener to remove: the recolor watch lives in useFrame, which
      // unmounts with the component. (Removing it here used to re-read __ORBIT_CONTROLS__,
      // so a live↔edit flip in between unsubscribed from the wrong object and leaked.)
      camWatchRef.current?.reset()
      isaacSplat.detachEngine()
      delete window.__ISAAC_SPLAT_COUNT__
      delete window.__OGS_HT_SET_CONTROL__
      delete window.__OGS_HT_ENGINE__
      delete window.__OGS_HEAD_TEETH__
      delete window.__OGS_BRANCHES__
      // A character switch changes the control space; a stale rest rig would
      // be added to the NEXT bundle's ARKit frames.
      restRigRef.current = null
      rigRangeRef.current = null
      resetRigConstrain()
      resetLiveFaceControls()
      window.__RIG_LIMITS__ = { state: 'none', range: null, limits: null }
      // Eye Follow: the next character owns its own capability, slots and aim
      // origin — a stale pivot would aim from the previous character's eyes.
      eyeFollowSlotsRef.current = null
      eyeFollowPivotRef.current = null
      declaredRangeRef.current = null
      setSplatEyeFollowCapability(null)
      resetEyeFollowScene()
      poseAnchorBlenderRef.current = null
      mouthPcaRef.current = null
      blinkMacroRef.current = null
      window.__MOUTH_SUBSPACE__ = { state: 'none' }
      window.__POSE_ANCHORS__ = { state: 'none', count: 0 }
      // Same rule for the mouth references — another bundle's rows would pose
      // the wrong controls with in-range values.
      liveMouthDriveRef.current = null
      // Every OGS pipeline: release the branches' ONNX sessions, gpu-buffer
      // tensors, plücker and lifter — a character switch otherwise leaks
      // decoder VRAM.
      if (liveBranches) {
        for (const br of liveBranches) { try { br.dispose() } catch { /* best-effort */ } }
        liveBranches = null
      }
      if (eyesMount) { eyesMount.dispose(); eyesMount = null; eyesMountRef.current = null }
      if (meshOverlay) { meshOverlay.dispose(); meshOverlay = null }
      if (updater) updater.dispose()
      if (ownedSpark) {
        scene.remove(ownedSpark)
        ownedSpark.dispose?.()
      }
      const avatar = window.__AVATAR_SCENE__
      if (avatar && avatarPrevVisible != null) avatar.visible = avatarPrevVisible
    }
  }, [gl, scene, camera])

  return null
}
